import React, { useState, useEffect, useId } from 'react';
import { API_URL, fmtFecha, SUCURSALES, SUCURSAL_NOMBRES, SUCURSAL_ESTADO, ESTADOS_MEXICO, MUNICIPIOS_POR_ESTADO, OPCIONES_SUCURSALES } from '../constants';
import { Input, Select, blobToBase64, fileToBase64, toYMDLocal } from './shared';
import { TabCapacitaciones, ModalSubirEscaneo } from './Capacitaciones';
import { resolverEstatusEmpleado } from '../utils/estatusEmpleado';
import { inicialesEmpleado } from '../utils/inicialesEmpleado';

const MOTIVOS_BAJA = [
  'Renuncia Voluntaria', 'Terminación de Contrato', 'Abandono de Empleo',
  'Despido Justificado', 'Despido Sin Causa', 'Fallecimiento',
  'Incapacidad Permanente', 'Mutuo Acuerdo', 'Otro'
];

// ════════════════════════════════════════════════════════════════════════════
// EMPLEADOS TABLA
// ════════════════════════════════════════════════════════════════════════════

// Buscador por nombre o ID. El ID puede llegar como número o como texto (según la celda
// de Sheets) y cualquiera de los dos puede faltar: se comparan siempre como texto.
// Un ID numérico pierde sus ceros iniciales (433 en vez de "00433"): además del texto
// recibido se compara su forma de 5 dígitos, para que "00433" y "0043" lo encuentren.
export function coincideBusquedaEmpleado(empleado, busqueda) {
  const termino = String(busqueda ?? '').toLowerCase();
  const nombre = String(empleado?.nombre ?? '').toLowerCase();
  const id = String(empleado?.id ?? '').trim().toLowerCase();
  const idCincoDigitos = /^\d{1,5}$/.test(id) ? id.padStart(5, '0') : id;
  return nombre.includes(termino) || id.includes(termino) || idCincoDigitos.includes(termino);
}

// Beneficiario (EMPLEADOS!AI): si el detalle no trajo la propiedad y no se capturó nada,
// se omite del payload para que el backend conserve la celda. '' sólo viaja como borrado
// explícito (el detalle sí traía el dato y el usuario lo vació).
export function payloadConBeneficiario(datos, detalle) {
  const payload = { ...datos };
  if ((detalle || {}).beneficiario_banregio === undefined && !payload.beneficiario_banregio) {
    delete payload.beneficiario_banregio;
  }
  return payload;
}

export function EmpleadosTabla({ empleados, titulo, busqueda, setBusqueda, onNuevo, onVerExpediente, onRegistro, onAltasBajas }) {
  const [sucursalActiva, setSucursalActiva] = useState('APO');
  const [ordenAsc, setOrdenAsc] = useState(true);
  const [filtroArea, setFiltroArea] = useState('');
  const [filtroPuesto, setFiltroPuesto] = useState('');

  const empleadosSucursal = empleados.filter(e => e.sucursal === sucursalActiva);
  const areasDisponibles = [...new Set(empleadosSucursal.map(e => e.area).filter(Boolean))].sort();
  const puestosDisponibles = [...new Set(
    empleadosSucursal.filter(e => filtroArea === '' || e.area === filtroArea).map(e => e.puesto).filter(Boolean)
  )].sort();

  const filtrados = empleadosSucursal
    .filter(e =>
      coincideBusquedaEmpleado(e, busqueda) &&
      (filtroArea === '' || e.area === filtroArea) &&
      (filtroPuesto === '' || e.puesto === filtroPuesto)
    )
    .sort((a, b) => {
      const numA = parseInt(a.id, 10);
      const numB = parseInt(b.id, 10);
      const comparacion = (isNaN(numA) || isNaN(numB))
        ? String(a.id).localeCompare(String(b.id))
        : numA - numB;
      return ordenAsc ? comparacion : -comparacion;
    });

  return (
    <div>
      <div className="page-header">
        <div className="page-title"><i className="ti ti-users"></i> {titulo}</div>
        {titulo === 'Empleados Activos' && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn-secondary" onClick={onRegistro} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <i className="ti ti-clipboard-list"></i> Registro del Día
            </button>
            <button className="btn-secondary" onClick={onAltasBajas} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <i className="ti ti-arrows-transfer-up"></i> Altas y Bajas
            </button>
            <button className="btn-primary" onClick={onNuevo}>
              <i className="ti ti-plus"></i> Nuevo Empleado
            </button>
          </div>
        )}
      </div>

      <div className="search-wrap" style={{ marginBottom: '20px', display: 'flex', gap: '8px', background: 'none', border: 'none', padding: 0, width: 'auto' }}>
        {SUCURSALES.map(s => (
          <button
            key={s}
            onClick={() => { setSucursalActiva(s); setFiltroArea(''); setFiltroPuesto(''); }}
            className={`btn-secondary`}
            style={{
              background: sucursalActiva === s ? '#1d3d6b' : '#e5e7eb',
              color: sucursalActiva === s ? 'white' : '#1f2937'
            }}
          >
            {SUCURSAL_NOMBRES[s]}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div className="search-wrap" style={{ marginBottom: 0 }}>
          <i className="ti ti-search"></i>
          <input
            type="text"
            placeholder="Buscar por ID o nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>

        <select
          value={filtroArea}
          onChange={(e) => { setFiltroArea(e.target.value); setFiltroPuesto(''); }}
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px' }}
        >
          <option value="">Todas las áreas</option>
          {areasDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
        </select>

        <select
          value={filtroPuesto}
          onChange={(e) => setFiltroPuesto(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px' }}
        >
          <option value="">Todos los puestos</option>
          {puestosDisponibles.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="card">
        <table className="tabla">
          <thead>
            <tr>
              <th className="sortable" onClick={() => setOrdenAsc(prev => !prev)}>
                ID {ordenAsc ? '▲' : '▼'}
              </th>
              <th>Nombre</th>
              <th>Área</th>
              <th>Puesto</th>
              <th style={{ width: '80px', textAlign: 'center' }}>Expediente</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map(emp => (
              <tr key={emp.id}>
                <td><strong>{emp.id}</strong></td>
                <td>{emp.nombre}</td>
                <td>{emp.area}</td>
                <td>{emp.puesto}</td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="btn-exp"
                    onClick={() => onVerExpediente && onVerExpediente(emp)}
                    title="Ver expediente"
                  >
                    <i className="ti ti-folder-open"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtrados.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No hay empleados que coincidan
          </div>
        )}
      </div>

      <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280' }}>
        Total: <strong>{filtrados.length}</strong> empleados
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL EMPLEADO
// ════════════════════════════════════════════════════════════════════════════

export function ModalEmpleado({ onClose, onCrear, empleados = [] }) {
  const [tab, setTab] = useState(1);
  const [empleadoCreadoId, setEmpleadoCreadoId] = useState(null);
  const [datos, setDatos] = useState({
    id_empleado: '', nombres: '', apellido_paterno: '', apellido_materno: '',
    sexo: '', estado_civil: '', telefono: '', email: '', tiene_hijos: 'No', numero_hijos: 0,
    curp: '', rfc: '', nss: '', sdi: '', salario_diario: '',
    sucursal_nomina: 'APO',
    ubicacion_final: '', centro_final: '', nodo_final: '', puesto: '', area: '',
    tipo_empleado: 'Operativo', tipo_contrato: '', fecha_ingreso: '',
    fecha_nacimiento: '', estado_nacimiento: '', escolaridad: '',
    dom_calle: '', dom_numero_ext: '', dom_numero_int: '', dom_colonia: '', dom_municipio: '',
    dom_estado: SUCURSAL_ESTADO['APO'], dom_cp: '',
    fiscal_cp_sat: '', contacto_emergencia_nombre: '', contacto_emergencia_parentesco: '', contacto_emergencia_telefono: '',
    beneficiario_banregio: '', lugar_nacimiento_b: '', fecha_nacimiento_b: '', telefono_b: '', parentesco_b: ''
  });
  const [enviando, setEnviando] = useState(false);
  const [sucursalesList, setSucursalesList] = useState([]);
  const [ubicacionesList, setUbicacionesList] = useState([]);
  const [centrosList, setCentrosList] = useState([]);
  const [nodosList, setNodosList] = useState([]);
  const [catalogoPuestos, setCatalogoPuestos] = useState([]);

  useEffect(() => {
    fetch(API_URL + '?path=api/catalogos')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setSucursalesList(d.sucursales || []);
          setUbicacionesList(d.ubicaciones_fisicas || []);
          setCentrosList(d.centros_costos || []);
          setNodosList(d.posiciones_nodos || []);
          setCatalogoPuestos(d.puestos || []);
        }
      })
      .catch(() => {});
  }, []);

  // Funciones cascada para jerarquía
  const obtenerUbicacionesPorSucursal = (sucursalId) => {
    if (!sucursalId) return [];
    const sucSelected = sucursalesList.find(s => s.ID_Sucursal === sucursalId);
    const sucursalNombre = sucSelected?.Nombre;
    if (!sucursalNombre) return [];
    return ubicacionesList.filter(u => u.Sucursal === sucursalNombre);
  };

  const obtenerCentrosPorUbicacion = (ubicacionId) => {
    if (!ubicacionId) return [];
    const ubicacion = ubicacionesList.find(u => u.ID_Ubicacion === ubicacionId);
    if (!ubicacion) return [];
    const centros = centrosList.filter(c => c.Ubicacion_Fisica === ubicacion.Nombre_Ubicacion);
    const seen = new Set();
    return centros.filter(c => {
      if (seen.has(c.Centro_Costos)) return false;
      seen.add(c.Centro_Costos);
      return true;
    });
  };

  const obtenerNodosPorCentro = (centroId) => {
    if (!centroId) return [];
    const centro = centrosList.find(c => c.ID_Centro === centroId);
    if (!centro) return [];
    return nodosList.filter(n => n.Centro_Costos && String(n.Centro_Costos).trim() === String(centro.Centro_Costos).trim());
  };

  const obtenerAreaDelCentro = (centroId) => {
    if (!centroId) return '';
    const centro = centrosList.find(c => c.ID_Centro === centroId);
    return centro?.AREA || centro?.Área || centro?.Area || '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDatos(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'sucursal_nomina') {
        next.dom_estado = SUCURSAL_ESTADO[value] || '';
        next.dom_municipio = '';
        next.ubicacion_final = '';
        next.centro_final = '';
        next.nodo_final = '';
        next.puesto = '';
      }
      if (name === 'ubicacion_final') {
        next.centro_final = '';
        next.nodo_final = '';
        next.puesto = '';
      }
      if (name === 'centro_final') {
        next.nodo_final = '';
        next.puesto = '';
        next.area = obtenerAreaDelCentro(value);
      }
      if (name === 'nodo_final') {
        const nodoSelected = obtenerNodosPorCentro(next.centro_final).find(n => n.Nodo_ID === value);
        next.puesto = nodoSelected?.Puesto || '';
      }
      if (name === 'dom_estado') next.dom_municipio = '';
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!datos.id_empleado || !datos.nombres || !datos.apellido_paterno || !datos.curp || !datos.rfc) {
      alert('⚠️ Completa los campos obligatorios');
      return;
    }

    setEnviando(true);
    try {
      console.log('📤 Enviando datos:', datos);
      const payload = encodeURIComponent(JSON.stringify(datos));
      console.log('📤 Payload (encoded):', payload.substring(0, 100) + '...');
      const url = API_URL + `?path=api/empleado/crear&payload=${payload}`;
      console.log('📤 URL:', url.substring(0, 150) + '...');
      const res = await fetch(url);
      const result = await res.json();

      if (result.success) {
        setEmpleadoCreadoId(datos.id_empleado);
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setEnviando(false);
    }
  };

  if (empleadoCreadoId) {
    return (
      <ModalFotoNuevoEmpleado
        idEmpleado={empleadoCreadoId}
        onDone={async () => { await onCrear(); }}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>Crear Nuevo Empleado</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-tabs">
          {[
            { num: 1, label: 'Identificación' },
            { num: 2, label: 'Documentos' },
            { num: 3, label: 'Domicilio' }
          ].map(t => (
            <button
              key={t.num}
              className={`modal-tab ${tab === t.num ? 'active' : ''}`}
              onClick={() => setTab(t.num)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {tab === 1 && <Tab1 datos={datos} handleChange={handleChange} />}
          {tab === 2 && (
            <Tab2
              datos={datos}
              handleChange={handleChange}
              sucursalesList={sucursalesList}
              ubicacionesList={ubicacionesList}
              centrosList={centrosList}
              nodosList={nodosList}
              obtenerUbicacionesPorSucursal={obtenerUbicacionesPorSucursal}
              obtenerCentrosPorUbicacion={obtenerCentrosPorUbicacion}
              obtenerNodosPorCentro={obtenerNodosPorCentro}
            />
          )}
          {tab === 3 && <Tab3 datos={datos} handleChange={handleChange} />}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={enviando}>
            {enviando ? 'Creando...' : '✓ Crear Empleado'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalFotoNuevoEmpleado({ idEmpleado, onDone }) {
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [subiendo, setSubiendo] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const size = 300;
        const canvas = document.createElement('canvas');
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext('2d');
        const lado = Math.min(img.width, img.height);
        const ox = (img.width - lado) / 2;
        const oy = (img.height - lado) / 2;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, ox, oy, lado, lado, 0, 0, size, size);

        // Versión rectangular para Check List
        const canvasRect = document.createElement('canvas');
        canvasRect.width = size; canvasRect.height = size;
        const ctxRect = canvasRect.getContext('2d');
        ctxRect.fillStyle = '#ffffff';
        ctxRect.fillRect(0, 0, size, size);
        ctxRect.drawImage(img, ox, oy, lado, lado, 0, 0, size, size);

        canvas.toBlob((blobCircle) => {
          canvasRect.toBlob((blobRect) => {
            Promise.all([blobToBase64(blobCircle), blobToBase64(blobRect)]).then(([b64c, b64r]) => {
              setPreview(URL.createObjectURL(blobCircle));
              setBase64({ circle: b64c.split(',')[1], rect: b64r.split(',')[1] });
            });
          }, 'image/jpeg', 0.9);
        }, 'image/png');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubir = async () => {
    if (!base64) return;
    setSubiendo(true);
    try {
      const fotoData = {
        path: 'api/empleado/foto',
        id_empleado: idEmpleado,
        base64: base64.circle || base64,
        nombre_archivo: 'foto_empleado.png',
        base64_rect: base64.rect || null
      };
      console.log('📤 Subiendo foto (POST con body)...');
      const res = await fetch(API_URL + '?path=api/empleado/foto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fotoData)
      });
      const result = await res.json();
      if (!result.success) alert('❌ Error: ' + result.error);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSubiendo(false);
      await onDone();
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h2><i className="ti ti-circle-check" style={{ color: '#10b981' }}></i> Empleado #{idEmpleado} creado</h2>
        </div>
        <div className="modal-body" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>¿Deseas agregar una foto ahora? (opcional)</p>
          {preview && (
            <img src={preview} alt="preview" style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '50%', marginBottom: '16px', border: '3px solid #0086ab' }} />
          )}
          <div>
            <label className="accion-btn accion-btn-blue" style={{ cursor: 'pointer', marginBottom: '10px', display: 'inline-flex' }}>
              <i className="ti ti-camera"></i>
              <span>Seleccionar foto</span>
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onDone}>Omitir</button>
          {base64 && (
            <button className="btn-primary" onClick={handleSubir} disabled={subiendo}>
              {subiendo ? 'Guardando...' : '📷 Guardar foto'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Tab1({ datos, handleChange }) {
  return (
    <div className="form-grid">
      <Input label="ID *" name="id_empleado" value={datos.id_empleado} onChange={handleChange} maxLength="5" />
      <Input label="Nombres *" name="nombres" value={datos.nombres} onChange={handleChange} />
      <Input label="Apellido Paterno *" name="apellido_paterno" value={datos.apellido_paterno} onChange={handleChange} />
      <Input label="Apellido Materno" name="apellido_materno" value={datos.apellido_materno} onChange={handleChange} />
      <Select label="Género" name="sexo" value={datos.sexo} onChange={handleChange} opciones={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Femenino' }]} />
      <Select label="Estado Civil" name="estado_civil" value={datos.estado_civil} onChange={handleChange} opciones={['Soltero', 'Casado', 'Unión libre', 'Viudo', 'Divorciado']} />
      <Input label="Teléfono" name="telefono" value={datos.telefono} onChange={handleChange} />
      <Input label="Email" name="email" value={datos.email} onChange={handleChange} type="email" />
      <Select label="Escolaridad" name="escolaridad" value={datos.escolaridad} onChange={handleChange} opciones={['Primaria', 'Secundaria', 'Preparatoria', 'Técnico', 'Licenciatura']} />
      <Select label="¿Tiene Hijos?" name="tiene_hijos" value={datos.tiene_hijos} onChange={handleChange} opciones={['Sí', 'No']} />
      {datos.tiene_hijos === 'Sí' && <Input label="Número de Hijos" name="numero_hijos" value={datos.numero_hijos} onChange={handleChange} type="number" />}
      <Input label="Fecha de Nacimiento" name="fecha_nacimiento" value={datos.fecha_nacimiento} onChange={handleChange} type="date" />
      <Select label="Estado de Nacimiento" name="estado_nacimiento" value={datos.estado_nacimiento} onChange={handleChange} opciones={ESTADOS_MEXICO} />
    </div>
  );
}

function Tab2({ datos, handleChange, sucursalesList, ubicacionesList, centrosList, nodosList, obtenerUbicacionesPorSucursal, obtenerCentrosPorUbicacion, obtenerNodosPorCentro }) {
  return (
    <div className="form-grid">
      <Input label="CURP *" name="curp" value={datos.curp} onChange={handleChange} maxLength="18" />
      <Input label="RFC *" name="rfc" value={datos.rfc} onChange={handleChange} maxLength="13" />
      <Input label="NSS" name="nss" value={datos.nss} onChange={handleChange} />
      <Input label="SDI" name="sdi" value={datos.sdi} onChange={handleChange} />
      <Input label="Salario Diario" name="salario_diario" value={datos.salario_diario} onChange={handleChange} type="number" step="0.01" />
      <Select label="Sucursal *" name="sucursal_nomina" value={datos.sucursal_nomina} onChange={handleChange} opciones={OPCIONES_SUCURSALES} />
      <Select
        label="Ubicación Física *"
        name="ubicacion_final"
        value={datos.ubicacion_final || ''}
        onChange={handleChange}
        opciones={obtenerUbicacionesPorSucursal(datos.sucursal_nomina).map(u => ({ value: u.ID_Ubicacion, label: u.Nombre_Ubicacion }))}
        disabled={!datos.sucursal_nomina}
      />
      <Select
        label="Centro de Costos *"
        name="centro_final"
        value={datos.centro_final || ''}
        onChange={handleChange}
        opciones={obtenerCentrosPorUbicacion(datos.ubicacion_final).map(c => ({ value: c.ID_Centro, label: c.Centro_Costos }))}
        disabled={!datos.ubicacion_final}
      />
      <Select
        label="Nodo *"
        name="nodo_final"
        value={datos.nodo_final || ''}
        onChange={handleChange}
        opciones={obtenerNodosPorCentro(datos.centro_final).map(n => ({ value: n.Nodo_ID, label: n.Nombre_Nodo }))}
        disabled={!datos.centro_final}
      />
      <Select
        label="Puesto"
        name="puesto"
        value={datos.puesto || ''}
        onChange={handleChange}
        opciones={datos.puesto ? [{ value: datos.puesto, label: datos.puesto }] : []}
        disabled={true}
      />
      <Select label="Tipo Empleado" name="tipo_empleado" value={datos.tipo_empleado} onChange={handleChange} opciones={['Operativo', 'Administrativo']} />
      <Select label="Tipo Contrato" name="tipo_contrato" value={datos.tipo_contrato} onChange={handleChange} opciones={['1er 3 meses', '2do 3 meses', 'Indefinido']} />
      <Input label="Fecha Ingreso" name="fecha_ingreso" value={datos.fecha_ingreso} onChange={handleChange} type="date" />
    </div>
  );
}

function Tab3({ datos, handleChange }) {
  const municipiosDisponibles = MUNICIPIOS_POR_ESTADO[datos.dom_estado] || [];

  return (
    <div className="form-grid">
      <Input label="Calle" name="dom_calle" value={datos.dom_calle} onChange={handleChange} />
      <Input label="Número Exterior" name="dom_numero_ext" value={datos.dom_numero_ext} onChange={handleChange} />
      <Input label="Número Interior" name="dom_numero_int" value={datos.dom_numero_int} onChange={handleChange} />
      <Input label="Colonia" name="dom_colonia" value={datos.dom_colonia} onChange={handleChange} />
      <Select label="Estado" name="dom_estado" value={datos.dom_estado} onChange={handleChange} opciones={Object.keys(MUNICIPIOS_POR_ESTADO)} />
      <Select label="Municipio" name="dom_municipio" value={datos.dom_municipio} onChange={handleChange} opciones={municipiosDisponibles} disabled={!datos.dom_estado} />
      <Input label="CP" name="dom_cp" value={datos.dom_cp} onChange={handleChange} />
      <Input label="CP SAT" name="fiscal_cp_sat" value={datos.fiscal_cp_sat} onChange={handleChange} />
      <h3 style={{ gridColumn: '1 / -1', marginTop: '10px' }}>Contacto de Emergencia</h3>
      <Input label="Nombre" name="contacto_emergencia_nombre" value={datos.contacto_emergencia_nombre} onChange={handleChange} />
      <Input label="Parentesco" name="contacto_emergencia_parentesco" value={datos.contacto_emergencia_parentesco} onChange={handleChange} />
      <Input label="Teléfono" name="contacto_emergencia_telefono" value={datos.contacto_emergencia_telefono} onChange={handleChange} />
      <h3 style={{ gridColumn: '1 / -1', marginTop: '10px' }}>Beneficiario Tarjeta Nómina</h3>
      <Input label="Nombre Beneficiario" name="beneficiario_banregio" value={datos.beneficiario_banregio} onChange={handleChange} />
      <Input label="Parentesco" name="parentesco_b" value={datos.parentesco_b} onChange={handleChange} />
      <Input label="Teléfono" name="telefono_b" value={datos.telefono_b} onChange={handleChange} />
      <Input label="Lugar de Nacimiento" name="lugar_nacimiento_b" value={datos.lugar_nacimiento_b} onChange={handleChange} placeholder="Ciudad, Estado" />
      <Input label="Fecha de Nacimiento" name="fecha_nacimiento_b" value={datos.fecha_nacimiento_b} onChange={handleChange} type="date" />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL EXPEDIENTE (Fase E.1 -- rediseño visual, sin tocar lógica)
//
// Estilos exclusivos del Expediente, inyectados localmente (mismo patrón ya
// usado en Evaluaciones.jsx para AcordeonOperativo/CierreEvaluacion): un
// <style> propio con clases .expediente-* y overrides ESCOPADOS con doble
// clase (".modal.expediente-modal ...") para nunca editar las reglas
// compartidas .modal-tab/.info-grid/.accion-btn/etc. de src/styles.js, que
// también usan Reclutamiento.jsx e ImportacionIntellisis.jsx.
// ════════════════════════════════════════════════════════════════════════════
const ESTILOS_EXPEDIENTE = `
.modal.expediente-modal { max-width: 1100px; border-top: 3px solid var(--secondary); }

/* Encabezado general del modal */
/* Franja superior compacta (solo contexto + cerrar; el nombre vive en el encabezado azul) */
.expediente-modal > .modal-header { padding: 8px 20px 8px 34px; min-height: 48px; border-bottom: 1px solid #e8eef7; }
.expediente-modal > .modal-header h2 { font-size: 17px; font-weight: 700; color: #12305c; display: flex; align-items: center; gap: 8px; line-height: 1; }
.expediente-modal > .modal-header h2 i { font-size: 20px; color: var(--secondary); }
.expediente-modal > .modal-header .modal-close { width: 32px; height: 32px; padding: 0; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; font-size: 15px; line-height: 1; color: #94a3b8; transition: background-color .12s ease, color .12s ease; }
.expediente-modal > .modal-header .modal-close:hover { background: #eef2fb; color: var(--primary); }
.expediente-modal > .modal-header .modal-close:focus-visible { outline: 2px solid var(--secondary); outline-offset: 1px; }
.expediente-modal > .modal-body { padding: 24px 28px; }

/* Barra de pestañas principal (las sub-pestañas del modo edición no se tocan) */
.modal-tabs.expediente-tabs { background: #ffffff; padding: 0 18px; justify-content: space-between; align-items: stretch; border-bottom: 1px solid #e8eef7; }
.expediente-tabs .modal-tab { position: relative; flex: 0 0 auto; white-space: nowrap; min-width: auto; padding: 11px 16px; display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 14px; font-weight: 600; color: #64748b; border: none; border-radius: 8px 8px 0 0; transition: background-color .12s ease, color .12s ease; }
.expediente-tabs .modal-tab i { font-size: 18px; color: #7d8ea8; transition: color .12s ease; }
.expediente-tabs .modal-tab:hover { background: #f1f8fc; color: #1d5f96; }
.expediente-tabs .modal-tab:hover i { color: var(--secondary); }
.expediente-tabs .modal-tab:focus-visible { outline: 2px solid var(--secondary); outline-offset: -2px; }
/* Tab activo: icono+texto en azul/cian y un indicador corto (ancho del contenido, no de la celda) */
.expediente-tabs .modal-tab.active { background: transparent; color: #12305c; font-weight: 700; }
.expediente-tabs .modal-tab.active i { color: var(--secondary); }
.expediente-tabs .modal-tab.active::after { content: ''; position: absolute; left: 16px; right: 16px; bottom: 0; height: 3px; border-radius: 3px 3px 0 0; background: var(--secondary); }

/* Encabezado azul del empleado */
.expediente-header { position: relative; overflow: hidden; display: flex; align-items: center; gap: 22px; padding: 26px 28px; background: linear-gradient(115deg, #0f2a52 0%, #1d3d6b 38%, #0a6f9a 72%, #1fb1d6 100%); border-radius: 14px; color: white; margin-bottom: 22px; box-shadow: 0 6px 18px rgba(15,42,82,.22); }
.expediente-header::before { content: ''; position: absolute; width: 320px; height: 320px; right: -80px; top: -140px; border-radius: 50%; background: radial-gradient(circle, rgba(255,255,255,.16) 0%, rgba(255,255,255,0) 70%); pointer-events: none; }
.expediente-header::after { content: ''; position: absolute; width: 220px; height: 220px; right: 190px; bottom: -130px; border-radius: 50%; border: 26px solid rgba(255,255,255,.06); pointer-events: none; }
.expediente-header > * { position: relative; z-index: 1; }
.expediente-avatar { width: 78px; height: 78px; border-radius: 50%; background: rgba(255,255,255,0.2); box-shadow: 0 0 0 4px rgba(255,255,255,.28); display: flex; align-items: center; justify-content: center; font-size: 27px; font-weight: 700; letter-spacing: 0.02em; overflow: hidden; }
.expediente-avatar-cam { position: absolute; bottom: -2px; right: -2px; width: 28px; height: 28px; border-radius: 50%; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.3); color: var(--secondary); font-size: 15px; }
.expediente-nombre { font-size: 23px; font-weight: 800; letter-spacing: 0.01em; line-height: 1.15; margin-bottom: 4px; }
.expediente-subtitulo { font-size: 14px; opacity: 0.88; margin-bottom: 10px; }
.expediente-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; color: white; }
.expediente-badge::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: rgba(255,255,255,.95); }
.expediente-badge-activo { background: #10b981; }
.expediente-badge-baja { background: #ef4444; }
.expediente-badge-neutro { background: #64748b; }
.expediente-header-der { text-align: right; }
.expediente-num { font-size: 30px; font-weight: 800; line-height: 1; margin-bottom: 8px; }
.expediente-sucursal { display: flex; align-items: center; justify-content: flex-end; gap: 6px; font-size: 13px; opacity: 0.9; margin-bottom: 14px; }
.expediente-btn-editar { display: inline-flex; align-items: center; gap: 7px; padding: 8px 16px; border-radius: 8px; border: 1px solid rgba(255,255,255,.6); background: rgba(255,255,255,0.12); color: white; font-size: 12px; cursor: pointer; font-weight: 600; transition: background-color .12s ease; }
.expediente-btn-editar:hover { background: rgba(255,255,255,0.26); }

/* Layout Personal (modo lectura): contenido principal ~70% + información rápida ~30% */
.expediente-layout { display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 3fr); gap: 22px; align-items: start; }
.expediente-main, .expediente-side { min-width: 0; }

.expediente-section { margin-bottom: 18px; border: 1px solid #dfe7f3; border-radius: 12px; background: #ffffff; padding: 18px 20px; box-shadow: 0 1px 3px rgba(29,61,107,.05); }
.expediente-main > .expediente-section:last-child { margin-bottom: 0; }
.expediente-section-title { font-size: 15px; font-weight: 700; color: #12305c; margin-bottom: 14px; display: flex; align-items: center; gap: 9px; }
.expediente-section-title i { font-size: 20px; color: var(--secondary); }
/* Secciones colapsables: el encabezado completo es un <button> */
.expediente-section-toggle { width: 100%; margin: 0; padding: 0; border: none; background: transparent; font-family: inherit; text-align: left; cursor: pointer; border-radius: 8px; }
.expediente-section-toggle:focus-visible { outline: 2px solid var(--secondary); outline-offset: 4px; }
.expediente-section-toggle .expediente-section-chevron { margin-left: auto; font-size: 18px; color: #94a3b8; transition: color .12s ease; }
.expediente-section-toggle:hover .expediente-section-chevron { color: var(--secondary); }
.expediente-section-cerrada .expediente-section-toggle { margin-bottom: 0; }
/* Filas de datos (estilo "Resumen de evaluación"): icono + etiqueta + valor + divisor,
   sobre el fondo de la tarjeta de sección (sin tarjeta individual por dato). Dos
   columnas internas en escritorio; el orden de lectura es por filas. */
.expediente-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); column-gap: 32px; }
.expediente-field { min-width: 0; display: flex; align-items: center; gap: 12px; padding: 11px 0; border-bottom: 1px solid #edf1f7; }
.expediente-field:last-child, .expediente-field:nth-last-child(2):nth-child(odd) { border-bottom: none; }
.expediente-field-icon { flex: 0 0 auto; width: 28px; height: 28px; border-radius: 8px; background: #e9f4fa; color: var(--secondary); display: inline-flex; align-items: center; justify-content: center; font-size: 16px; }
.expediente-field-label { flex: 0 0 auto; font-size: 13px; font-weight: 500; color: #64748b; }
.expediente-field-value { flex: 1; min-width: 0; text-align: right; font-size: 14px; font-weight: 700; color: #12305c; overflow-wrap: anywhere; }

/* Información rápida (panel resumen, columna derecha) */
.expediente-quick { border: 1px solid #dfe7f3; border-radius: 12px; background: #ffffff; padding: 18px 20px; box-shadow: 0 1px 3px rgba(29,61,107,.05); }
.expediente-quick .expediente-section-title { padding-bottom: 12px; border-bottom: 1px solid #e8eef7; margin-bottom: 4px; }
.expediente-quick-row { display: flex; align-items: center; gap: 10px; padding: 12px 0; border-bottom: 1px solid #eef2f8; min-width: 0; }
.expediente-quick-row:last-child { border-bottom: none; padding-bottom: 2px; }
.expediente-quick-icon { flex: 0 0 auto; width: 30px; height: 30px; border-radius: 8px; background: #e4f1f8; color: var(--secondary); display: inline-flex; align-items: center; justify-content: center; font-size: 16px; }
.expediente-quick-label { flex: 0 0 auto; font-size: 13px; font-weight: 500; color: #64748b; }
.expediente-quick-value { flex: 1; min-width: 0; text-align: right; font-size: 14px; font-weight: 700; color: #12305c; overflow-wrap: anywhere; }

/* Bloques laterales reservados (solo diseño, sin funcionalidad todavía) */
.expediente-side > * + * { margin-top: 18px; }
.expediente-placeholder { border: 1px solid #dfe7f3; border-radius: 12px; background: #ffffff; padding: 18px 20px; box-shadow: 0 1px 3px rgba(29,61,107,.05); }
.expediente-placeholder .expediente-section-title { padding-bottom: 12px; border-bottom: 1px solid #e8eef7; margin-bottom: 14px; }
.expediente-placeholder-caja { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 6px; padding: 18px 14px; border: 1px dashed #cfdaea; border-radius: 10px; background: #f8fafd; color: #64748b; }
.expediente-placeholder-caja > i { font-size: 26px; color: #a9bbd3; }
.expediente-placeholder-titulo { font-size: 13px; font-weight: 600; color: #475569; }
.expediente-placeholder-texto { font-size: 12.5px; line-height: 1.45; color: #64748b; }

/* Pestaña Documentos: tarjetas de trabajo (mismo lenguaje que Personal) */
.expediente-docs { display: flex; flex-direction: column; gap: 14px; }
.expediente-doc-card { border: 1px solid #dfe7f3; border-radius: 12px; background: #ffffff; padding: 14px 18px; box-shadow: 0 1px 3px rgba(29,61,107,.05); }
.expediente-doc-card .expediente-section-title { margin-bottom: 2px; }
.expediente-doc-desc { font-size: 12.5px; line-height: 1.4; color: #64748b; margin: 0 0 0 29px; }
/* Cabecera de tarjeta: texto a la izquierda, acciones a la derecha (baja en angosto) */
.expediente-doc-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px 16px; }
.expediente-doc-head-texto { flex: 1 1 260px; min-width: 0; }
.expediente-doc-head-conborde { padding-bottom: 12px; border-bottom: 1px solid #e8eef7; }
.expediente-doc-acciones { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.expediente-doc-cuerpo { margin-top: 12px; }

/* Paquete de ingreso: tres "action cards" compactas */
.expediente-doc-acciones-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 12px; margin-top: 12px; }
.expediente-docs .expediente-doc-accion { display: flex; align-items: center; gap: 12px; min-width: 0; padding: 12px 14px; border: 1px solid #dfe7f3; border-radius: 10px; background: #ffffff; font-family: inherit; text-align: left; cursor: pointer; transition: background-color .14s ease, border-color .14s ease, box-shadow .14s ease, transform .14s ease; }
.expediente-docs .expediente-doc-accion:hover { border-color: #8ec6de; box-shadow: 0 2px 8px rgba(29,61,107,.10); transform: translateY(-1px); }
.expediente-docs .expediente-doc-accion:active { transform: none; box-shadow: none; }
.expediente-docs .expediente-doc-accion:focus-visible { outline: 2px solid var(--secondary); outline-offset: 2px; }
.expediente-doc-accion-icono { flex: 0 0 auto; width: 38px; height: 38px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: #e4f1f8; color: #0a7fa6; font-size: 20px; }
.expediente-doc-accion-cuerpo { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.expediente-doc-accion-titulo { font-size: 13.5px; font-weight: 700; color: #12305c; line-height: 1.25; }
.expediente-doc-accion-desc { font-size: 12px; font-weight: 400; color: #64748b; line-height: 1.3; }
/* Principal: descargar paquete (cian institucional, algo más de presencia) */
.expediente-docs .expediente-doc-accion-principal { background: #f2f9fd; border-color: #b6dcec; }
.expediente-docs .expediente-doc-accion-principal .expediente-doc-accion-icono { background: linear-gradient(180deg, #0a83aa 0%, #076d90 100%); color: #ffffff; }
/* Auxiliar: copiar texto (verde discreto) */
.expediente-docs .expediente-doc-accion-auxiliar { background: #f6fdf8; border-color: #c6f0d3; }
.expediente-docs .expediente-doc-accion-auxiliar:hover { border-color: #86efac; box-shadow: 0 2px 8px rgba(22,101,52,.10); }
.expediente-docs .expediente-doc-accion-auxiliar .expediente-doc-accion-icono { background: #dcfce7; color: #16a34a; }
/* Deshabilitado / en curso: neutro, sin hover ni elevación */
.expediente-docs .expediente-doc-accion:disabled, .expediente-docs .expediente-doc-accion:disabled:hover { background: #f1f5f9; border-color: #e2e8f0; box-shadow: none; transform: none; cursor: wait; }
.expediente-docs .expediente-doc-accion:disabled .expediente-doc-accion-icono { background: #e2e8f0; color: #94a3b8; }
.expediente-docs .expediente-doc-accion:disabled .expediente-doc-accion-titulo, .expediente-docs .expediente-doc-accion:disabled .expediente-doc-accion-desc { color: #94a3b8; }

/* Acciones compactas (Adjuntar documento, Subir registro, Ver registro): sistema propio, sin .accion-btn */
.expediente-docs .expediente-doc-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 14px; border-radius: 10px; border: 1px solid; font-family: inherit; font-size: 13px; font-weight: 600; line-height: 1; white-space: nowrap; text-decoration: none; cursor: pointer; transition: background-color .14s ease, border-color .14s ease, box-shadow .14s ease, transform .14s ease, color .14s ease; }
.expediente-docs .expediente-doc-btn i { font-size: 17px; flex: 0 0 auto; }
.expediente-docs .expediente-doc-btn:focus-visible { outline: 2px solid var(--secondary); outline-offset: 2px; }
.expediente-docs .expediente-doc-btn:active { transform: none; box-shadow: none; }
.expediente-docs .expediente-doc-primaria { background: linear-gradient(180deg, #0a83aa 0%, #076d90 100%); border-color: #06607f; color: #ffffff; box-shadow: 0 1px 2px rgba(6,96,127,.25); }
.expediente-docs .expediente-doc-primaria:hover { background: linear-gradient(180deg, #0c8fb9 0%, #087399 100%); border-color: #055670; box-shadow: 0 3px 8px rgba(6,96,127,.28); transform: translateY(-1px); }
.expediente-docs .expediente-doc-secundaria { background: #f5fafd; border-color: #b6dcec; color: #1d3d6b; }
.expediente-docs .expediente-doc-secundaria i { color: #0a7fa6; }
.expediente-docs .expediente-doc-secundaria:hover { background: #e9f4fa; border-color: #8ec6de; box-shadow: 0 2px 6px rgba(29,61,107,.10); transform: translateY(-1px); }
.expediente-docs .expediente-doc-btn:has(input:disabled), .expediente-docs .expediente-doc-btn:has(input:disabled):hover { background: #f1f5f9; border-color: #e2e8f0; color: #94a3b8; box-shadow: none; transform: none; cursor: wait; }
.expediente-docs .expediente-doc-btn:has(input:disabled) i { color: #94a3b8; }

.expediente-doc-lista { display: flex; flex-direction: column; gap: 8px; }
.expediente-doc-vacio { display: flex; flex-direction: column; align-items: center; gap: 1px; padding: 9px 12px; border: 1px dashed #cfdaea; border-radius: 10px; background: #f8fafd; text-align: center; }
.expediente-doc-vacio > i { font-size: 20px; color: #a9bbd3; }
.expediente-doc-vacio-titulo { font-size: 13px; font-weight: 600; color: #475569; }
.expediente-doc-vacio-texto { font-size: 12px; color: #64748b; }
.expediente-doc-cargando { color: #9ca3af; font-size: 13px; }

/* Pestaña Cambios: historial compacto (solo lectura) */
.expediente-cambios-card { border: 1px solid #dfe7f3; border-radius: 12px; background: #ffffff; padding: 14px 18px; box-shadow: 0 1px 3px rgba(29,61,107,.05); }
.expediente-cambios-head { display: flex; align-items: center; justify-content: space-between; gap: 10px 16px; flex-wrap: wrap; padding-bottom: 12px; border-bottom: 1px solid #e8eef7; }
.expediente-cambios-head-texto { flex: 1 1 260px; min-width: 0; }
.expediente-cambios-card .expediente-section-title { margin-bottom: 2px; }
.expediente-cambios-desc { font-size: 12.5px; line-height: 1.4; color: #64748b; margin: 0 0 0 29px; }
.expediente-cambios-total { flex: 0 0 auto; padding: 3px 11px; border-radius: 20px; background: #e4f1f8; color: #0a6f9a; font-size: 12px; font-weight: 700; }
.expediente-cambios-lista { display: flex; flex-direction: column; }
.expediente-cambio-item { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 18px; padding: 12px 0; border-bottom: 1px solid #edf1f7; }
.expediente-cambio-item:last-child { border-bottom: none; padding-bottom: 2px; }
.expediente-cambio-tipo { flex: 1 1 200px; min-width: 0; display: flex; align-items: center; gap: 12px; }
.expediente-cambio-icono { flex: 0 0 auto; width: 32px; height: 32px; border-radius: 9px; background: #e4f1f8; color: var(--secondary); display: inline-flex; align-items: center; justify-content: center; font-size: 17px; }
.expediente-cambio-titulo { font-size: 14px; font-weight: 700; color: #12305c; overflow-wrap: anywhere; }
.expediente-cambio-valores { flex: 2 1 320px; min-width: 0; display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; gap: 12px; }
.expediente-cambio-valor { min-width: 0; }
.expediente-cambio-etiqueta { font-size: 11.5px; font-weight: 500; color: #64748b; margin-bottom: 2px; }
.expediente-cambio-dato { font-size: 13.5px; font-weight: 500; color: #475569; overflow-wrap: anywhere; }
.expediente-cambio-nuevo .expediente-cambio-dato { font-size: 14px; font-weight: 700; color: #12305c; }
.expediente-cambio-flecha { color: #a9bbd3; font-size: 18px; }
.expediente-cambio-estatus { flex: 0 0 auto; }
.expediente-cambios-vacio { display: flex; flex-direction: column; align-items: center; gap: 2px; margin-top: 12px; padding: 14px 12px; border: 1px dashed #cfdaea; border-radius: 10px; background: #f8fafd; text-align: center; }
.expediente-cambios-vacio > i { font-size: 22px; color: #a9bbd3; }
.expediente-cambios-vacio-titulo { font-size: 13px; font-weight: 600; color: #475569; }
.expediente-cambios-vacio-texto { font-size: 12px; color: #64748b; }

/* Pestaña Acciones: centro de acciones (lista compacta de filas) */
.expediente-acciones-card { border: 1px solid #dfe7f3; border-radius: 12px; background: #ffffff; padding: 14px 18px; box-shadow: 0 1px 3px rgba(29,61,107,.05); }
.expediente-acciones-head { padding-bottom: 12px; border-bottom: 1px solid #e8eef7; }
.expediente-acciones-card .expediente-section-title { margin-bottom: 2px; }
.expediente-acciones-desc { font-size: 12.5px; line-height: 1.4; color: #64748b; margin: 0 0 0 29px; }
.expediente-acciones-lista { display: flex; flex-direction: column; }
.expediente-accion-item { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 14px; padding: 12px 0; border-bottom: 1px solid #edf1f7; }
.expediente-accion-item:last-child { border-bottom: none; padding-bottom: 2px; }
.expediente-accion-icon { flex: 0 0 auto; width: 36px; height: 36px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-size: 19px; background: #e4f1f8; color: #0a7fa6; }
.expediente-accion-info { flex: 1 1 260px; min-width: 0; }
.expediente-accion-titulo { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px; font-size: 14px; font-weight: 700; color: #12305c; overflow-wrap: anywhere; }
.expediente-accion-tag { padding: 1px 8px; border-radius: 20px; background: #f1f5f9; color: #64748b; font-size: 11px; font-weight: 600; }
.expediente-accion-desc { font-size: 12.5px; line-height: 1.4; color: #64748b; margin-top: 1px; overflow-wrap: anywhere; }
.expediente-accion-control { flex: 0 0 auto; }
.expediente-accion-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 34px; padding: 0 14px; border-radius: 9px; border: 1px solid; font-family: inherit; font-size: 13px; font-weight: 600; line-height: 1; white-space: nowrap; text-decoration: none; cursor: pointer; transition: background-color .14s ease, border-color .14s ease, color .14s ease; }
.expediente-accion-btn i { font-size: 16px; }
.expediente-accion-btn:focus-visible { outline: 2px solid var(--secondary); outline-offset: 2px; }
/* Normal / utilidad */
.expediente-accion-neutro .expediente-accion-btn { background: #f5fafd; border-color: #b6dcec; color: #1d3d6b; }
.expediente-accion-neutro .expediente-accion-btn:hover { background: #e9f4fa; border-color: #8ec6de; }
/* Delicada: baja (rojo solo como acento) */
.expediente-accion-peligro .expediente-accion-icon { background: #fee2e2; color: #dc2626; }
.expediente-accion-peligro .expediente-accion-btn { background: #fff5f5; border-color: #fecaca; color: #b91c1c; }
.expediente-accion-peligro .expediente-accion-btn:hover { background: #fee2e2; border-color: #fca5a5; }
/* Reversión (mismo ámbar que ya usaba la pestaña) */
.expediente-accion-aviso .expediente-accion-icon { background: #fef3c7; color: #b45309; }
.expediente-accion-aviso .expediente-accion-btn { background: #fffbeb; border-color: #fde68a; color: #92400e; }
.expediente-accion-aviso .expediente-accion-btn:hover { background: #fef3c7; border-color: #fcd34d; }
/* Reingreso (mismo verde que ya usaba la pestaña) */
.expediente-accion-positivo .expediente-accion-icon { background: #d1fae5; color: #059669; }
.expediente-accion-positivo .expediente-accion-btn { background: #ecfdf5; border-color: #6ee7b7; color: #065f46; }
.expediente-accion-positivo .expediente-accion-btn:hover { background: #d1fae5; border-color: #34d399; }
/* No disponible */
.expediente-accion-off .expediente-accion-icon { background: #f1f5f9; color: #94a3b8; }
.expediente-accion-off .expediente-accion-titulo { color: #64748b; }
.expediente-accion-off .expediente-accion-btn, .expediente-accion-off .expediente-accion-btn:hover { background: #f1f5f9; border-color: #e2e8f0; color: #94a3b8; cursor: not-allowed; }

/* Pestaña Capacitaciones (markup en Capacitaciones.jsx / TabCapacitaciones) */
.expediente-cap-card { border: 1px solid #dfe7f3; border-radius: 12px; background: #ffffff; padding: 14px 18px; box-shadow: 0 1px 3px rgba(29,61,107,.05); }
.expediente-cap-head { display: flex; align-items: center; justify-content: space-between; gap: 10px 16px; flex-wrap: wrap; padding-bottom: 12px; border-bottom: 1px solid #e8eef7; }
.expediente-cap-head-texto { flex: 1 1 260px; min-width: 0; }
.expediente-cap-card .expediente-section-title { margin-bottom: 2px; }
.expediente-cap-desc { font-size: 12.5px; line-height: 1.4; color: #64748b; margin: 0 0 0 29px; }
.expediente-cap-total { flex: 0 0 auto; padding: 3px 11px; border-radius: 20px; background: #e4f1f8; color: #0a6f9a; font-size: 12px; font-weight: 700; }
.expediente-cap-lista { display: flex; flex-direction: column; }
.expediente-cap-item { border-bottom: 1px solid #edf1f7; }
.expediente-cap-item:last-child { border-bottom: none; }
.expediente-cap-fila { width: 100%; display: flex; align-items: center; gap: 12px; padding: 12px 8px; margin: 0; border: none; border-radius: 8px; background: transparent; font-family: inherit; text-align: left; color: inherit; cursor: pointer; transition: background-color .12s ease; }
.expediente-cap-fila:hover { background: #f6fafd; }
.expediente-cap-fila:focus-visible { outline: 2px solid var(--secondary); outline-offset: -2px; }
.expediente-cap-icono { flex: 0 0 auto; width: 34px; height: 34px; border-radius: 9px; background: #e4f1f8; color: var(--secondary); display: inline-flex; align-items: center; justify-content: center; font-size: 18px; }
.expediente-cap-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.expediente-cap-nombre { font-size: 14px; font-weight: 700; color: #12305c; overflow-wrap: anywhere; }
.expediente-cap-meta { font-size: 12.5px; color: #64748b; overflow-wrap: anywhere; }
.expediente-cap-chevron { flex: 0 0 auto; font-size: 17px; color: #94a3b8; }
.expediente-cap-abierto .expediente-cap-chevron { color: var(--secondary); }
.expediente-cap-detalle { margin: 2px 0 14px 54px; padding: 12px 14px; border-radius: 10px; background: #f8fafd; }
.expediente-cap-resumen { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px 18px; padding-bottom: 12px; margin-bottom: 12px; border-bottom: 1px solid #e6edf6; }
.expediente-cap-dato { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.expediente-cap-etiqueta { font-size: 11px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: #64748b; }
.expediente-cap-dato strong { font-size: 13.5px; font-weight: 700; color: #12305c; overflow-wrap: anywhere; }
.expediente-cap-registros { display: flex; flex-direction: column; gap: 8px; }
.expediente-cap-registro { display: flex; align-items: center; gap: 12px; padding: 9px 12px; border: 1px solid #e3eaf5; border-radius: 9px; background: #ffffff; }
.expediente-cap-reg-icono { flex: 0 0 auto; width: 28px; height: 28px; border-radius: 8px; background: #e4f1f8; color: var(--secondary); display: inline-flex; align-items: center; justify-content: center; font-size: 16px; }
.expediente-cap-reg-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.expediente-cap-reg-info strong { font-size: 13.5px; font-weight: 700; color: #12305c; overflow-wrap: anywhere; }
.expediente-cap-reg-info span { font-size: 12px; color: #64748b; }
.expediente-cap-reg-acciones { flex: 0 0 auto; display: flex; align-items: center; gap: 6px; }
.expediente-cap-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 30px; padding: 0 11px; border-radius: 8px; border: 1px solid #b6dcec; background: #f5fafd; color: #1d3d6b; font-size: 12.5px; font-weight: 600; line-height: 1; text-decoration: none; white-space: nowrap; transition: background-color .12s ease, border-color .12s ease; }
.expediente-cap-btn i { font-size: 15px; color: #0a7fa6; }
.expediente-cap-btn:hover { background: #e9f4fa; border-color: #8ec6de; }
.expediente-cap-btn:focus-visible { outline: 2px solid var(--secondary); outline-offset: 2px; }
.expediente-cap-btn-icono { width: 30px; padding: 0; }
.expediente-cap-pendiente { display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-radius: 8px; background: #fef3c7; color: #92400e; font-size: 12.5px; font-weight: 500; }
.expediente-cap-vacio { display: flex; flex-direction: column; align-items: center; gap: 2px; margin-top: 12px; padding: 14px 12px; border: 1px dashed #cfdaea; border-radius: 10px; background: #f8fafd; text-align: center; }
.expediente-cap-vacio > i { font-size: 22px; color: #a9bbd3; }
.expediente-cap-vacio-titulo { font-size: 13px; font-weight: 600; color: #475569; }
.expediente-cap-vacio-texto { font-size: 12px; color: #64748b; }

/* Responsive: la información rápida baja debajo del contenido principal (no se oculta) */
/* Con el contenido principal angosto (modal < ~1080px) las filas pasan a 1 columna interna */
@media (max-width: 1200px) {
  .expediente-fields { grid-template-columns: minmax(0, 1fr); }
  .expediente-field:nth-last-child(2):nth-child(odd) { border-bottom: 1px solid #edf1f7; }
}
@media (max-width: 900px) {
  .expediente-layout { grid-template-columns: minmax(0, 1fr); }
  .expediente-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .expediente-field:nth-last-child(2):nth-child(odd) { border-bottom: none; }
}
@media (max-width: 640px) {
  .expediente-header { flex-wrap: wrap; padding: 20px; }
  .expediente-header-der { text-align: left; width: 100%; }
  .expediente-sucursal { justify-content: flex-start; }
  .expediente-modal > .modal-header { padding-left: 20px; }
  .expediente-modal > .modal-body { padding: 18px 16px; }
  .expediente-doc-card { padding: 12px 14px; }
  .expediente-cambios-card { padding: 12px 14px; }
  .expediente-acciones-card { padding: 12px 14px; }
  .expediente-cap-card { padding: 12px 14px; }
  .expediente-cap-detalle { margin-left: 0; }
  .expediente-cap-resumen { grid-template-columns: minmax(0, 1fr); }
  .expediente-cap-registro { flex-wrap: wrap; }
  .expediente-cap-reg-acciones { flex-basis: 100%; padding-left: 40px; }
  .expediente-accion-control { flex-basis: 100%; padding-left: 50px; }
  .expediente-cambio-valores { grid-template-columns: minmax(0, 1fr); gap: 6px; }
  .expediente-cambio-flecha { transform: rotate(90deg); justify-self: start; }
  .expediente-doc-acciones-grid { grid-template-columns: minmax(0, 1fr); }
  .modal-tabs.expediente-tabs { padding: 0 8px; }
  .expediente-tabs .modal-tab { padding: 11px 12px; }
  .expediente-tabs .modal-tab.active::after { left: 12px; right: 12px; }
}
@media (max-width: 700px) {
  .expediente-fields { grid-template-columns: minmax(0, 1fr); }
  .expediente-field:nth-last-child(2):nth-child(odd) { border-bottom: 1px solid #edf1f7; }
}
`;

// Sección con título (equivalente local a SeccionLabel + .info-grid,
// exclusivo del Expediente -- SeccionLabel de shared.jsx se deja intacto
// para Documentos, que no forma parte del alcance de E.1).
// Cada sección se abre/cierra de forma independiente (estado local, sin
// persistencia). `abiertaInicial` solo define el estado al montar.
function ExpSeccion({ icon, titulo, abiertaInicial = false, children }) {
  const [abierta, setAbierta] = useState(abiertaInicial);
  const idContenido = useId();
  return (
    <div className={`expediente-section${abierta ? '' : ' expediente-section-cerrada'}`}>
      <button
        type="button"
        className="expediente-section-title expediente-section-toggle"
        aria-expanded={abierta}
        aria-controls={idContenido}
        onClick={() => setAbierta(a => !a)}
      >
        <i className={`ti ${icon}`}></i> {titulo}
        <i className={`ti ${abierta ? 'ti-chevron-up' : 'ti-chevron-down'} expediente-section-chevron`} aria-hidden="true"></i>
      </button>
      {abierta && <div className="expediente-fields" id={idContenido}>{children}</div>}
    </div>
  );
}

// Bloque lateral reservado (solo diseño): sin datos, sin controles, sin guardado.
function ExpPlaceholder({ icon, titulo, icono, mensaje, detalle, dataAttr }) {
  return (
    <div className="expediente-placeholder" {...{ [dataAttr]: true }}>
      <div className="expediente-section-title"><i className={`ti ${icon}`}></i> {titulo}</div>
      <div className="expediente-placeholder-caja">
        <i className={`ti ${icono}`} aria-hidden="true"></i>
        <div className="expediente-placeholder-titulo">{mensaje}</div>
        <div className="expediente-placeholder-texto">{detalle}</div>
      </div>
    </div>
  );
}

// Estatus mostrado en Personal (header + Información rápida): ver
// resolverEstatusEmpleado en src/utils/estatusEmpleado.js (compartido con Mobile).

// Tarjeta "Información rápida" (columna derecha del modo lectura de
// Personal). Solo repite datos que YA se muestran en Datos Laborales /
// encabezado -- resumen intencionalmente redundante, sin campos nuevos ni
// datos calculados. Aspecto de panel resumen (fila icono + etiqueta + valor).
function ExpInfoRapida({ empleado, d, estatusInfo }) {
  const filas = [
    ['ti-map-pin', 'Sucursal', SUCURSAL_NOMBRES[d.sucursal_nomina || empleado.sucursal] || '—'],
    ['ti-building', 'Área', d.area || empleado.area || '—'],
    ['ti-user', 'Puesto', d.puesto || empleado.puesto || '—'],
    ['ti-users', 'Tipo Empleado', d.tipo_empleado || '—'],
    ['ti-file-text', 'Tipo Contrato', d.tipo_contrato || '—']
  ];
  return (
    <div className="expediente-quick" data-info-rapida>
      <div className="expediente-section-title"><i className="ti ti-bolt"></i> Información rápida</div>
      <div className="expediente-quick-row">
        <span className="expediente-quick-icon"><i className="ti ti-circle-check"></i></span>
        <span className="expediente-quick-label">Estatus</span>
        <span className="expediente-quick-value">
          <span className={`expediente-badge expediente-badge-${estatusInfo.tipo}`}>{estatusInfo.texto}</span>
        </span>
      </div>
      {filas.map(([icono, label, valor]) => (
        <div className="expediente-quick-row" key={label}>
          <span className="expediente-quick-icon"><i className={`ti ${icono}`}></i></span>
          <span className="expediente-quick-label">{label}</span>
          <span className="expediente-quick-value">{valor}</span>
        </div>
      ))}
    </div>
  );
}

// Campo individual (equivalente local a InfoCampo, exclusivo del
// Expediente). Conserva el mismo criterio de "—" para valores faltantes.
// `icono` es puramente decorativo (Tabler Icons, ya cargado en index.html).
function ExpCampo({ label, valor, highlight, mono, icono }) {
  return (
    <div className="expediente-field">
      {icono && <span className="expediente-field-icon"><i className={`ti ${icono}`}></i></span>}
      <span className="expediente-field-label">{label}</span>
      <span
        className="expediente-field-value"
        style={{
          color: highlight ? 'var(--secondary)' : undefined,
          fontFamily: mono ? 'monospace' : undefined
        }}
      >
        {valor}
      </span>
    </div>
  );
}

export function ModalExpediente({ empleado, cambios, onClose, onBaja }) {
  const [tab, setTab] = useState(1);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(true);
  const [confirmandoBaja, setConfirmandoBaja] = useState(false);
  const [aplicandoBaja, setAplicandoBaja] = useState(false);
  const [tipoBaja, setTipoBaja] = useState('Baja_A_Operativa');
  const [fechaBaja, setFechaBaja] = useState(() => toYMDLocal(new Date()));
  const [motivoBaja, setMotivoBaja] = useState('Renuncia Voluntaria');
  const [comentariosBaja, setComentariosBaja] = useState('');
  const [confirmandoRevertir, setConfirmandoRevertir] = useState(false);
  const [revirtiendo, setRevirtiendo] = useState(false);
  const [reingresoAbierto, setReingresoAbierto] = useState(false);
  const [fotoUrl, setFotoUrl] = useState(null);

  const recargarDetalle = async () => {
    setLoadingDetalle(true);
    try {
      const res = await fetch(API_URL + `?path=api/empleado/detalle&id=${empleado.id}`);
      const data = await res.json();
      if (data.success) {
        setDetalle(data.empleado);
        console.log('📷 Detalle empleado:', { foto_url: data.empleado.foto_url, foto_id: data.empleado.foto_id });
        if (data.empleado.foto_url) setFotoUrl(data.empleado.foto_url);
      }
    } catch {}
    finally { setLoadingDetalle(false); }
  };

  useEffect(() => { recargarDetalle(); }, [empleado.id]);

  const handleBaja = async () => {
    setAplicandoBaja(true);
    try {
      const payload = { id_empleado: empleado.id, tipo_baja: tipoBaja, fecha_baja: fechaBaja };
      if (tipoBaja === 'Baja_A_Operativa') {
        payload.motivo_baja = motivoBaja;
        payload.comentarios = comentariosBaja;
      }
      const url = API_URL + `?path=api/empleado/baja&payload=${encodeURIComponent(JSON.stringify(payload))}`;
      const res = await fetch(url);
      const result = await res.json();
      if (result.success) {
        alert('✅ Baja aplicada correctamente');
        setComentariosBaja('');
        setMotivoBaja('Renuncia Voluntaria');
        setFechaBaja(new Date().toISOString().slice(0, 10));
        await onBaja();
      } else {
        alert('❌ Error: ' + result.error);
        setConfirmandoBaja(false);
      }
    } catch (err) {
      alert('Error: ' + err.message);
      setConfirmandoBaja(false);
    } finally {
      setAplicandoBaja(false);
    }
  };

  const handleRevertir = async () => {
    setRevirtiendo(true);
    try {
      const url = API_URL + `?path=api/empleado/revertir-baja&payload=${encodeURIComponent(JSON.stringify({ id_empleado: empleado.id }))}`;
      const res = await fetch(url);
      const result = await res.json();
      if (result.success) {
        alert('✅ ' + result.mensaje);
        setConfirmandoRevertir(false);
        await onBaja();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setRevirtiendo(false);
    }
  };

  const imprimirComprobante = () => {
    const d = detalle || {};
    const hoy = new Date().toLocaleDateString('es-MX');
    const folio = 'AL-' + empleado.id + '-' + new Date().getFullYear();
    const nombre = (d.nombre_completo || empleado.nombre || '').toUpperCase();
    const sucNombre = SUCURSAL_NOMBRES[d.sucursal_nomina || empleado.sucursal] || empleado.sucursal || '';
    const html = `
      <html><head><title>Comprobante de Alta</title>
      <style>
        body { font-family: Arial, sans-serif; max-width: 320px; margin: 20px auto; }
        .ticket { border: 2px solid #000; padding: 20px; }
        h1 { text-align: center; font-size: 13px; margin: 4px 0; }
        .field { margin: 8px 0; font-size: 12px; }
        .label { font-weight: bold; }
        hr { border: 1px solid #000; margin: 10px 0; }
        @media print { body { margin: 0; } }
      </style></head>
      <body><div class="ticket">
        <h1>ACEROS LEAL S.A. DE C.V.</h1>
        <h1>COMPROBANTE DE ALTA</h1>
        <hr>
        <div class="field"><span class="label">Nombre:</span> ${nombre}</div>
        <div class="field"><span class="label"># Empleado:</span> ${empleado.id}</div>
        <div class="field"><span class="label">Puesto:</span> ${(empleado.puesto || '').toUpperCase()}</div>
        <div class="field"><span class="label">Área:</span> ${(empleado.area || '').toUpperCase()}</div>
        <div class="field"><span class="label">Sucursal:</span> ${sucNombre.toUpperCase()}</div>
        <div class="field"><span class="label">Salario Diario:</span> $${d.salario_diario || '—'}</div>
        <div class="field"><span class="label">Fecha Alta:</span> ${hoy}</div>
        <hr>
        <div class="field"><span class="label">Folio:</span> ${folio}</div>
        <hr>
      </div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  const copiarTextoEmail = () => {
    const d = detalle || {};
    const nombre = (d.nombre_completo || empleado.nombre || '').toUpperCase();
    const sucNombre = SUCURSAL_NOMBRES[d.sucursal_nomina || empleado.sucursal] || empleado.sucursal || '';
    const texto = `Nombre: ${nombre}\n# empleado: ${empleado.id}\nPuesto: ${(empleado.puesto || '').toUpperCase()}\nSucursal: ${sucNombre.toUpperCase()}`;
    navigator.clipboard.writeText(texto).then(() => alert('✅ Copiado al portapapeles'));
  };

  const tabs = [
    { num: 1, icon: 'ti-user', label: 'Personal' },
    { num: 2, icon: 'ti-paperclip', label: 'Documentos' },
    { num: 3, icon: 'ti-arrows-transfer-up', label: 'Cambios' },
    { num: 4, icon: 'ti-alert-triangle', label: 'Acciones' },
    { num: 5, icon: 'ti-calendar-stats', label: 'Incidencias' },
    { num: 6, icon: 'ti-school', label: 'Capacitaciones' },
  ];

  return (
    <div className="modal-overlay">
      <div className="modal expediente-modal">
        <style>{ESTILOS_EXPEDIENTE}</style>
        <div className="modal-header">
          <h2><i className="ti ti-folder-open"></i> Expediente</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-tabs expediente-tabs" style={{ overflowX: 'auto' }}>
          {tabs.map(t => (
            <button
              key={t.num}
              className={`modal-tab ${tab === t.num ? 'active' : ''}`}
              onClick={() => setTab(t.num)}
            >
              <i className={`ti ${t.icon}`}></i> {t.label}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {tab === 1 && (
            loadingDetalle
              ? <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Cargando...</div>
              : <ExpInfoPersonal detalle={detalle} empleado={empleado} fotoUrl={fotoUrl} onFotoChange={setFotoUrl} onActualizar={recargarDetalle} />
          )}
          {tab === 2 && (
            <ExpDocumentacion
              empleado={empleado}
              detalle={detalle}
              onComprobante={imprimirComprobante}
              onCopiar={copiarTextoEmail}
            />
          )}
          {tab === 3 && <ExpCambios cambios={cambios} />}
          {tab === 4 && (
            <ExpAcciones
              detalle={detalle}
              onBaja={(siguienteTipo) => {
                setTipoBaja(siguienteTipo || 'Baja_A_Operativa');
                setFechaBaja(new Date().toISOString().slice(0, 10));
                setConfirmandoBaja(true);
              }}
              onRevertir={() => setConfirmandoRevertir(true)}
              onReingreso={() => setReingresoAbierto(true)}
            />
          )}
          {tab === 5 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              <i className="ti ti-clipboard-list" style={{ fontSize: '36px', marginBottom: '10px', display: 'block' }}></i>
              Sin registros aún
            </div>
          )}
          {tab === 6 && (
            <TabCapacitaciones empleadoId={empleado.id} />
          )}
        </div>

        {confirmandoBaja && (
          <div className="modal-overlay" onClick={() => !aplicandoBaja && setConfirmandoBaja(false)}>
            <div className="modal" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2><i className="ti ti-user-minus"></i> Confirmar Baja</h2>
                <button className="modal-close" onClick={() => setConfirmandoBaja(false)} disabled={aplicandoBaja}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '6px' }}>Aplicar baja a:</p>
                  <p style={{ fontWeight: 700, fontSize: '17px', color: '#1d3d6b', marginBottom: '2px' }}>{empleado.nombre}</p>
                  <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '16px' }}>ID: {empleado.id}</p>
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label>Fecha de Baja</label>
                  <input type="date" value={fechaBaja} onChange={e => setFechaBaja(e.target.value)} max={new Date().toISOString().slice(0,10)} />
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label>Tipo de Baja</label>
                  <select value={tipoBaja} onChange={e => setTipoBaja(e.target.value)}>
                    <option value="Baja_A_Operativa">Baja A — Operativa (salida de operaciones)</option>
                    <option value="Baja_B_Sistema">Baja B — Sistema (baja en SIGRH)</option>
                    <option value="Baja_C_IMSS">Baja C — IMSS (trámite ante IMSS)</option>
                  </select>
                </div>
                {tipoBaja === 'Baja_A_Operativa' && (
                  <>
                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label>Motivo de Baja *</label>
                      <select value={motivoBaja} onChange={e => setMotivoBaja(e.target.value)}>
                        {MOTIVOS_BAJA.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label>Comentarios <span style={{ fontWeight: 400, color: '#9ca3af' }}>(opcional)</span></label>
                      <textarea
                        value={comentariosBaja}
                        onChange={e => setComentariosBaja(e.target.value)}
                        rows={2}
                        placeholder="Observaciones adicionales..."
                        style={{ width: '100%', resize: 'vertical', fontSize: '13px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit' }}
                      />
                    </div>
                  </>
                )}
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px' }}>
                  <p style={{ color: '#dc2626', fontWeight: 600, fontSize: '12px', margin: 0 }}>
                    {tipoBaja === 'Baja_A_Operativa' && '⚠️ El empleado dejará de aparecer como activo en operaciones.'}
                    {tipoBaja === 'Baja_B_Sistema' && '⚠️ Se registrará la baja formal en el sistema. Asegúrate de haber aplicado Baja A previamente.'}
                    {tipoBaja === 'Baja_C_IMSS' && '⚠️ Se registrará el trámite de baja ante el IMSS. Asegúrate de haber aplicado Baja B previamente.'}
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setConfirmandoBaja(false)} disabled={aplicandoBaja}>Cancelar</button>
                <button className="btn-danger" onClick={handleBaja} disabled={aplicandoBaja}>
                  {aplicandoBaja ? 'Aplicando...' : '🚪 Confirmar Baja'}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmandoRevertir && (
          <div className="modal-overlay" onClick={() => !revirtiendo && setConfirmandoRevertir(false)}>
            <div className="modal" style={{ maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Revertir Baja</h2>
                <button className="modal-close" onClick={() => setConfirmandoRevertir(false)} disabled={revirtiendo}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px' }}>
                  Se revertirá la baja de <strong>{empleado.nombre}</strong>.
                </p>
                {detalle?.estatus === 'Baja_A_Operativa' && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#92400e' }}>
                    El empleado regresará a <strong>Activo</strong> y se eliminará el registro de baja.
                  </div>
                )}
                {detalle?.estatus === 'Baja_B_Sistema' && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#92400e' }}>
                    El empleado regresará a <strong>Baja A — Operativa</strong>. Se limpiará la fecha de Baja B y el finiquito generado.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setConfirmandoRevertir(false)} disabled={revirtiendo}>Cancelar</button>
                <button className="btn-primary" onClick={handleRevertir} disabled={revirtiendo}>
                  {revirtiendo ? 'Revirtiendo...' : '↩ Confirmar Reversión'}
                </button>
              </div>
            </div>
          </div>
        )}

        {reingresoAbierto && (
          <ModalReingreso
            empleado={empleado}
            detalle={detalle}
            onClose={() => setReingresoAbierto(false)}
            onDone={async () => { setReingresoAbierto(false); await onBaja(); }}
          />
        )}
      </div>
    </div>
  );
}

function ExpInfoPersonal({ detalle, empleado, fotoUrl, onFotoChange, onActualizar }) {
  const d = detalle || {};
  const nombreCompleto = d.nombre_completo || empleado.nombre;
  const iniciales = inicialesEmpleado(nombreCompleto);
  const estatusInfo = resolverEstatusEmpleado(detalle, empleado);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [fotoError, setFotoError] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [tabEdit, setTabEdit] = useState(1);
  const [guardando, setGuardando] = useState(false);
  const [formData, setFormData] = useState({});
  const [catalogoAreas, setCatalogoAreas] = useState([]);
  const [catalogoPuestos, setCatalogoPuestos] = useState([]);

  const iniciarEdicion = () => {
    fetch(API_URL + '?path=api/catalogos')
      .then(r => r.json())
      .then(res => { if (res.success) { setCatalogoAreas(res.areas || []); setCatalogoPuestos(res.puestos || []); } })
      .catch(() => {});
    setFormData({
      id_empleado: String(d.id || empleado.id),
      nombres: d.nombres || '', apellido_paterno: d.apellido_paterno || '', apellido_materno: d.apellido_materno || '',
      sexo: d.sexo || '', estado_civil: d.estado_civil || '', escolaridad: d.escolaridad || '',
      telefono: d.telefono || '', email: d.email || '',
      tiene_hijos: d.tiene_hijos || 'No', numero_hijos: d.numero_hijos || 0,
      fecha_nacimiento: d.fecha_nacimiento ? String(d.fecha_nacimiento).slice(0, 10) : '',
      curp: d.curp || '', rfc: d.rfc || '', nss: d.nss || '',
      sucursal_nomina: d.sucursal_nomina || empleado.sucursal || '',
      area: d.area || empleado.area || '', puesto: d.puesto || empleado.puesto || '',
      tipo_empleado: d.tipo_empleado || '', tipo_contrato: d.tipo_contrato || '',
      dom_calle: d.dom_calle || '', dom_numero_ext: d.dom_numero_ext || '', dom_numero_int: d.dom_numero_int || '',
      dom_colonia: d.dom_colonia || '', dom_municipio: d.dom_municipio || '',
      dom_estado: d.dom_estado || '', dom_cp: d.dom_cp || '', fiscal_cp_sat: d.fiscal_cp_sat || '',
      estado_nacimiento: d.estado_nacimiento || '',
      contacto_emergencia_nombre: d.contacto_emergencia_nombre || '',
      contacto_emergencia_parentesco: d.contacto_emergencia_parentesco || '',
      contacto_emergencia_telefono: d.contacto_emergencia_telefono || '',
      beneficiario_banregio: d.beneficiario_banregio || '',
      parentesco_b: d.parentesco_b || '',
      telefono_b: d.telefono_b || '',
      lugar_nacimiento_b: d.lugar_nacimiento_b || '',
      fecha_nacimiento_b: d.fecha_nacimiento_b ? String(d.fecha_nacimiento_b).slice(0, 10) : ''
    });
    setTabEdit(1);
    setModoEdicion(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'sucursal_nomina') next.dom_estado = SUCURSAL_ESTADO[value] || prev.dom_estado;
      if (name === 'dom_estado') next.dom_municipio = '';
      return next;
    });
  };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const payload = encodeURIComponent(JSON.stringify(payloadConBeneficiario(formData, d)));
      const res = await fetch(API_URL + `?path=api/empleado/editar&payload=${payload}`);
      const result = await res.json();
      if (result.success) {
        alert('✅ Datos actualizados correctamente');
        setModoEdicion(false);
        if (onActualizar) onActualizar();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleFotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const size = 300;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // Recorte circular centrado con transparencia
        const lado = Math.min(img.width, img.height);
        const ox = (img.width - lado) / 2;
        const oy = (img.height - lado) / 2;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, ox, oy, lado, lado, 0, 0, size, size);

        canvas.toBlob(async (blobCircle) => {
          // Versión rectangular para Check List
          const canvasRect = document.createElement('canvas');
          canvasRect.width = size; canvasRect.height = size;
          const ctxRect = canvasRect.getContext('2d');
          ctxRect.fillStyle = '#ffffff';
          ctxRect.fillRect(0, 0, size, size);
          ctxRect.drawImage(img, ox, oy, lado, lado, 0, 0, size, size);

          canvasRect.toBlob(async (blobRect) => {
            const b64Circle = await blobToBase64(blobCircle);
            const b64Rect   = await blobToBase64(blobRect);
            setSubiendoFoto(true);
            try {
              const fotoData = {
                path: 'api/empleado/foto',
                id_empleado: empleado.id,
                base64: b64Circle.split(',')[1],
                nombre_archivo: 'foto_empleado.png',
                base64_rect: b64Rect.split(',')[1]
              };
              const res = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(fotoData)
              });
              const result = await res.json();
              if (result.success) {
                setFotoError(false);
                onFotoChange(result.foto_url);
              } else {
                alert('❌ Error al subir foto: ' + result.error);
              }
            } catch (err) {
              alert('Error: ' + err.message);
            } finally {
              setSubiendoFoto(false);
            }
          }, 'image/jpeg', 0.9);
        }, 'image/png');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const municipiosEdit = MUNICIPIOS_POR_ESTADO[formData.dom_estado] || [];

  if (modoEdicion) {
    return (
      <div>
        {/* Header edición */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '12px 16px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px' }}>
          <span style={{ fontWeight: 600, color: '#92400e', fontSize: '13px' }}>Modo edición — #{empleado.id} {nombreCompleto.toUpperCase()}</span>
          <button className="btn-secondary" style={{ padding: '6px 14px', fontSize: '12px' }} onClick={() => setModoEdicion(false)} disabled={guardando}>Cancelar</button>
        </div>

        {/* Tabs edición */}
        <div className="modal-tabs" style={{ marginBottom: '16px' }}>
          {[{ num: 1, label: 'Personal' }, { num: 2, label: 'Laboral' }, { num: 3, label: 'Domicilio' }].map(t => (
            <button key={t.num} className={`modal-tab ${tabEdit === t.num ? 'active' : ''}`} onClick={() => setTabEdit(t.num)}>{t.label}</button>
          ))}
        </div>

        {tabEdit === 1 && (
          <div className="form-grid">
            <Input label="Nombres *" name="nombres" value={formData.nombres} onChange={handleEditChange} />
            <Input label="Apellido Paterno *" name="apellido_paterno" value={formData.apellido_paterno} onChange={handleEditChange} />
            <Input label="Apellido Materno" name="apellido_materno" value={formData.apellido_materno} onChange={handleEditChange} />
            <Select label="Género" name="sexo" value={formData.sexo} onChange={handleEditChange} opciones={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Femenino' }]} />
            <Select label="Estado Civil" name="estado_civil" value={formData.estado_civil} onChange={handleEditChange} opciones={['Soltero', 'Casado', 'Unión libre', 'Viudo', 'Divorciado']} />
            <Select label="Escolaridad" name="escolaridad" value={formData.escolaridad} onChange={handleEditChange} opciones={['Primaria', 'Secundaria', 'Preparatoria', 'Técnico', 'Licenciatura']} />
            <Input label="Teléfono" name="telefono" value={formData.telefono} onChange={handleEditChange} />
            <Input label="Email" name="email" value={formData.email} onChange={handleEditChange} type="email" />
            <Select label="¿Tiene Hijos?" name="tiene_hijos" value={formData.tiene_hijos} onChange={handleEditChange} opciones={['Sí', 'No']} />
            {formData.tiene_hijos === 'Sí' && <Input label="Número de Hijos" name="numero_hijos" value={formData.numero_hijos || ''} onChange={handleEditChange} type="number" />}
            <Input label="Fecha de Nacimiento" name="fecha_nacimiento" value={formData.fecha_nacimiento} onChange={handleEditChange} type="date" />
            <Select label="Estado de Nacimiento" name="estado_nacimiento" value={formData.estado_nacimiento || ''} onChange={handleEditChange} opciones={ESTADOS_MEXICO} />
          </div>
        )}

        {tabEdit === 2 && (
          <div className="form-grid">
            <Input label="CURP" name="curp" value={formData.curp} onChange={handleEditChange} maxLength="18" />
            <Input label="RFC" name="rfc" value={formData.rfc} onChange={handleEditChange} maxLength="13" />
            <Input label="NSS" name="nss" value={formData.nss} onChange={handleEditChange} />
            <Select label="Sucursal" name="sucursal_nomina" value={formData.sucursal_nomina} onChange={handleEditChange} opciones={OPCIONES_SUCURSALES} />
            <Select label="Área" name="area" value={formData.area} onChange={handleEditChange} opciones={catalogoAreas} />
            <Select label="Puesto" name="puesto" value={formData.puesto} onChange={handleEditChange} opciones={catalogoPuestos} />
            <Select label="Tipo Empleado" name="tipo_empleado" value={formData.tipo_empleado} onChange={handleEditChange} opciones={['Operativo', 'Administrativo']} />
            <Select label="Tipo Contrato" name="tipo_contrato" value={formData.tipo_contrato} onChange={handleEditChange} opciones={['1er 3 meses', '2do 3 meses', 'Indefinido']} />
          </div>
        )}

        {tabEdit === 3 && (
          <div className="form-grid">
            <Input label="Calle" name="dom_calle" value={formData.dom_calle} onChange={handleEditChange} />
            <Input label="Número Exterior" name="dom_numero_ext" value={formData.dom_numero_ext} onChange={handleEditChange} />
            <Input label="Número Interior" name="dom_numero_int" value={formData.dom_numero_int} onChange={handleEditChange} />
            <Input label="Colonia" name="dom_colonia" value={formData.dom_colonia} onChange={handleEditChange} />
            <Select label="Estado" name="dom_estado" value={formData.dom_estado} onChange={handleEditChange} opciones={Object.keys(MUNICIPIOS_POR_ESTADO)} />
            <Select label="Municipio" name="dom_municipio" value={formData.dom_municipio} onChange={handleEditChange} opciones={municipiosEdit} disabled={!formData.dom_estado} />
            <Input label="CP" name="dom_cp" value={formData.dom_cp} onChange={handleEditChange} />
            <Input label="CP SAT" name="fiscal_cp_sat" value={formData.fiscal_cp_sat} onChange={handleEditChange} />
            <h3 style={{ gridColumn: '1 / -1', marginTop: '10px', fontSize: '13px', color: '#374151' }}>Contacto de Emergencia</h3>
            <Input label="Nombre" name="contacto_emergencia_nombre" value={formData.contacto_emergencia_nombre} onChange={handleEditChange} />
            <Input label="Parentesco" name="contacto_emergencia_parentesco" value={formData.contacto_emergencia_parentesco} onChange={handleEditChange} />
            <Input label="Teléfono" name="contacto_emergencia_telefono" value={formData.contacto_emergencia_telefono} onChange={handleEditChange} />
            <h3 style={{ gridColumn: '1 / -1', marginTop: '10px', fontSize: '13px', color: '#374151' }}>Beneficiario Tarjeta Nómina</h3>
            <Input label="Nombre Beneficiario" name="beneficiario_banregio" value={formData.beneficiario_banregio || ''} onChange={handleEditChange} />
            <Input label="Parentesco" name="parentesco_b" value={formData.parentesco_b || ''} onChange={handleEditChange} />
            <Input label="Teléfono" name="telefono_b" value={formData.telefono_b || ''} onChange={handleEditChange} />
            <Input label="Lugar de Nacimiento" name="lugar_nacimiento_b" value={formData.lugar_nacimiento_b || ''} onChange={handleEditChange} placeholder="Ciudad, Estado" />
            <Input label="Fecha de Nacimiento" name="fecha_nacimiento_b" value={formData.fecha_nacimiento_b || ''} onChange={handleEditChange} type="date" />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          <button className="btn-secondary" onClick={() => setModoEdicion(false)} disabled={guardando}>Cancelar</button>
          <button className="btn-primary" onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : '✓ Guardar Cambios'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Encabezado de perfil */}
      <div className="expediente-header" data-expediente-header>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div className="expediente-avatar">
            {fotoUrl && !fotoError
              ? <img
                  src={fotoUrl}
                  alt="foto"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={() => setFotoError(true)}
                />
              : iniciales
            }
          </div>
          <label className="expediente-avatar-cam" title="Cambiar foto">
            {subiendoFoto ? '⏳' : <i className="ti ti-camera"></i>}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFotoSelect} disabled={subiendoFoto} />
          </label>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="expediente-nombre">{nombreCompleto.toUpperCase()}</div>
          <div className="expediente-subtitulo">{empleado.puesto} · {empleado.area}</div>
          <span className={`expediente-badge expediente-badge-${estatusInfo.tipo}`} data-estatus-badge>
            {estatusInfo.texto}
          </span>
        </div>
        <div className="expediente-header-der">
          <div className="expediente-num">#{empleado.id}</div>
          <div className="expediente-sucursal"><i className="ti ti-map-pin"></i><span>{SUCURSAL_NOMBRES[empleado.sucursal] || empleado.sucursal}</span></div>
          <button className="expediente-btn-editar" onClick={iniciarEdicion} data-editar-datos>
            <i className="ti ti-pencil"></i><span>Editar Datos</span>
          </button>
        </div>
      </div>

      <div className="expediente-layout">
      <div className="expediente-main">
      {/* Datos Laborales */}
      <ExpSeccion icon="ti-briefcase" titulo="Datos Laborales" abiertaInicial>
        <ExpCampo icono="ti-building" label="Área" valor={d.area || empleado.area || '—'} />
        <ExpCampo icono="ti-user" label="Puesto" valor={d.puesto || empleado.puesto || '—'} />
        <ExpCampo icono="ti-map-pin" label="Sucursal" valor={SUCURSAL_NOMBRES[d.sucursal_nomina || empleado.sucursal] || '—'} />
        <ExpCampo icono="ti-users" label="Tipo Empleado" valor={d.tipo_empleado || '—'} />
        <ExpCampo icono="ti-file-text" label="Tipo Contrato" valor={d.tipo_contrato || '—'} />
        <ExpCampo icono="ti-currency-dollar" label="Salario Diario" valor={d.salario_diario ? '$' + Number(d.salario_diario).toLocaleString('es-MX', { minimumFractionDigits: 2 }) : '—'} highlight />
        <ExpCampo icono="ti-calendar" label="Fecha Ingreso" valor={fmtFecha(empleado.fecha_ingreso)} />
      </ExpSeccion>

      {/* Datos de Identificación */}
      <ExpSeccion icon="ti-id-badge" titulo="Datos de Identificación">
        <ExpCampo icono="ti-id" label="RFC" valor={d.rfc || '—'} mono />
        <ExpCampo icono="ti-id" label="CURP" valor={d.curp || '—'} mono />
        <ExpCampo icono="ti-shield" label="NSS" valor={d.nss || '—'} mono />
        <ExpCampo icono="ti-phone" label="Teléfono" valor={d.telefono || '—'} />
        <ExpCampo icono="ti-mail" label="Email" valor={d.email || '—'} />
        <ExpCampo icono="ti-gender-bigender" label="Género" valor={d.sexo === 'M' ? 'Masculino' : d.sexo === 'F' ? 'Femenino' : d.sexo || '—'} />
        <ExpCampo icono="ti-heart" label="Estado Civil" valor={d.estado_civil || '—'} />
        <ExpCampo icono="ti-school" label="Escolaridad" valor={d.escolaridad || '—'} />
      </ExpSeccion>

      {/* Domicilio */}
      <ExpSeccion icon="ti-home" titulo="Domicilio">
        <ExpCampo icono="ti-home" label="Calle y Número" valor={[d.dom_calle, d.dom_numero_ext ? '#' + d.dom_numero_ext : '', d.dom_numero_int ? 'Int. ' + d.dom_numero_int : ''].filter(Boolean).join(' ') || '—'} />
        <ExpCampo icono="ti-map" label="Colonia" valor={d.dom_colonia || '—'} />
        <ExpCampo icono="ti-building-community" label="Municipio" valor={d.dom_municipio || '—'} />
        <ExpCampo icono="ti-map-2" label="Estado" valor={d.dom_estado || '—'} />
        <ExpCampo icono="ti-map-pin" label="CP" valor={d.dom_cp || '—'} />
        <ExpCampo icono="ti-file-text" label="CP SAT" valor={d.fiscal_cp_sat || '—'} />
      </ExpSeccion>

      {/* Contacto emergencia */}
      {(d.contacto_emergencia_nombre || d.contacto_emergencia_telefono) && (
        <ExpSeccion icon="ti-phone" titulo="Contacto de Emergencia">
          <ExpCampo icono="ti-user" label="Nombre" valor={d.contacto_emergencia_nombre || '—'} />
          <ExpCampo icono="ti-users" label="Parentesco" valor={d.contacto_emergencia_parentesco || '—'} />
          <ExpCampo icono="ti-phone" label="Teléfono" valor={d.contacto_emergencia_telefono || '—'} />
        </ExpSeccion>
      )}

      {/* Beneficiario tarjeta nómina */}
      {d.beneficiario_banregio && (
        <ExpSeccion icon="ti-credit-card" titulo="Beneficiario Tarjeta Nómina">
          <ExpCampo icono="ti-user" label="Nombre" valor={d.beneficiario_banregio || '—'} />
          <ExpCampo icono="ti-users" label="Parentesco" valor={d.parentesco_b || '—'} />
          <ExpCampo icono="ti-phone" label="Teléfono" valor={d.telefono_b || '—'} />
          <ExpCampo icono="ti-map-pin" label="Lugar de Nacimiento" valor={d.lugar_nacimiento_b || '—'} />
          <ExpCampo icono="ti-calendar" label="Fecha de Nacimiento" valor={d.fecha_nacimiento_b || '—'} />
        </ExpSeccion>
      )}
      </div>
      <aside className="expediente-side">
        <ExpInfoRapida empleado={empleado} d={d} estatusInfo={estatusInfo} />
        <ExpPlaceholder dataAttr="data-anotaciones" icon="ti-notes" titulo="Anotaciones" icono="ti-note" mensaje="Sin anotaciones registradas." detalle="Las anotaciones de RH estarán disponibles en una próxima implementación." />
        <ExpPlaceholder dataAttr="data-papeleria" icon="ti-folder" titulo="Papelería del expediente" icono="ti-files" mensaje="El estatus de documentación se mostrará aquí." detalle="Pendiente de implementación." />
      </aside>
      </div>
    </div>
  );
}

function ExpDocumentacion({ empleado, detalle, onComprobante, onCopiar }) {
  const [generandoPaquete, setGenerandoPaquete] = useState(false);
  const [anexos, setAnexos] = useState([]);
  const [loadingAnexos, setLoadingAnexos] = useState(true);
  const [subiendoAnexo, setSubiendoAnexo] = useState(false);
  const [modalEscaneo, setModalEscaneo] = useState(false);

  useEffect(() => {
    cargarAnexos();
  }, [empleado.id]);

  const cargarAnexos = async () => {
    setLoadingAnexos(true);
    try {
      const res = await fetch(API_URL + `?path=api/empleado/anexos&id=${empleado.id}`);
      const data = await res.json();
      if (data.success) setAnexos(data.anexos || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAnexos(false);
    }
  };

  const handleDescargarPaquete = async () => {
    setGenerandoPaquete(true);
    try {
      const url = API_URL + `?path=api/empleado/paquete&payload=${encodeURIComponent(JSON.stringify({ id_empleado: empleado.id }))}`;
      const res = await fetch(url);
      const result = await res.json();
      if (result.success) {
        window.open(result.zip_url, '_blank');
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setGenerandoPaquete(false);
    }
  };

  const handleSubirAnexo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setSubiendoAnexo(true);
    try {
      const base64 = await fileToBase64(file);
      const body = JSON.stringify({
        id_empleado: empleado.id,
        base64: base64.split(',')[1],
        nombre_archivo: file.name
      });
      const res = await fetch(API_URL + '?path=api/empleado/anexar', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
      const result = await res.json();
      if (result.success) {
        await cargarAnexos();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSubiendoAnexo(false);
    }
  };

  const handleEliminarAnexo = async (fileId, nombre) => {
    if (!window.confirm(`¿Eliminar "${nombre}"?`)) return;
    try {
      const url = API_URL + `?path=api/empleado/eliminar-anexo&payload=${encodeURIComponent(JSON.stringify({ file_id: fileId }))}`;
      const res = await fetch(url);
      const result = await res.json();
      if (result.success) {
        setAnexos(prev => prev.filter(a => a.id !== fileId));
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div className="expediente-docs">
      {/* Paquete de Ingreso */}
      <div className="expediente-doc-card">
        <div className="expediente-doc-head">
          <div className="expediente-doc-head-texto">
            <div className="expediente-section-title"><i className="ti ti-package"></i> Paquete de ingreso</div>
            <p className="expediente-doc-desc">Genera y prepara la documentación necesaria para el ingreso del empleado.</p>
          </div>
        </div>
        <div className="expediente-doc-acciones-grid">
          <button type="button" className="expediente-doc-accion expediente-doc-accion-principal" onClick={handleDescargarPaquete} disabled={generandoPaquete}>
            <span className="expediente-doc-accion-icono"><i className="ti ti-file-zip"></i></span>
            <span className="expediente-doc-accion-cuerpo">
              <span className="expediente-doc-accion-titulo">{generandoPaquete ? 'Generando ZIP...' : 'Descargar Paquete de Ingreso'}</span>
              <span className="expediente-doc-accion-desc">Genera el paquete completo</span>
            </span>
          </button>
          <button type="button" className="expediente-doc-accion" onClick={onComprobante}>
            <span className="expediente-doc-accion-icono"><i className="ti ti-printer"></i></span>
            <span className="expediente-doc-accion-cuerpo">
              <span className="expediente-doc-accion-titulo">Comprobante de Alta</span>
              <span className="expediente-doc-accion-desc">Genera el comprobante del empleado</span>
            </span>
          </button>
          <button type="button" className="expediente-doc-accion expediente-doc-accion-auxiliar" onClick={onCopiar}>
            <span className="expediente-doc-accion-icono"><i className="ti ti-clipboard-copy"></i></span>
            <span className="expediente-doc-accion-cuerpo">
              <span className="expediente-doc-accion-titulo">Copiar texto para email</span>
              <span className="expediente-doc-accion-desc">Copia el mensaje para envío</span>
            </span>
          </button>
        </div>
      </div>

      {/* Documentos Firmados */}
      <div className="expediente-doc-card" data-docs-firmados>
        <div className="expediente-doc-head expediente-doc-head-conborde">
          <div className="expediente-doc-head-texto">
            <div className="expediente-section-title"><i className="ti ti-file-description"></i> Documentos firmados</div>
            <p className="expediente-doc-desc">Documentos escaneados o firmados asociados al expediente.</p>
          </div>
          <div className="expediente-doc-acciones">
            <label className="expediente-doc-btn expediente-doc-primaria" style={{ cursor: subiendoAnexo ? 'wait' : 'pointer' }}>
              <i className="ti ti-plus"></i>
              <span>{subiendoAnexo ? 'Subiendo...' : 'Adjuntar Documento'}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleSubirAnexo} disabled={subiendoAnexo} />
            </label>
          </div>
        </div>

        <div className="expediente-doc-cuerpo">
          {loadingAnexos ? (
            <div className="expediente-doc-cargando">Cargando documentos...</div>
          ) : anexos.length === 0 ? (
            <div className="expediente-doc-vacio" data-docs-vacio>
              <i className="ti ti-file-description" aria-hidden="true"></i>
              <div className="expediente-doc-vacio-titulo">Sin documentos anexados</div>
              <div className="expediente-doc-vacio-texto">Los documentos firmados aparecerán aquí.</div>
            </div>
          ) : (
            <div className="expediente-doc-lista">
              {anexos.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e8ecf0' }}>
                <i className="ti ti-file" style={{ color: '#0086ab', flexShrink: 0 }}></i>
                <span style={{ flex: 1, fontSize: '13px', color: '#1d3d6b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nombre}</span>
                <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>{new Date(a.fecha).toLocaleDateString('es-MX')}</span>
                <a href={a.url} target="_blank" rel="noreferrer" style={{ color: '#0086ab', fontSize: '13px', textDecoration: 'none', flexShrink: 0 }} title="Ver">
                  <i className="ti ti-external-link"></i>
                </a>
                <button onClick={() => handleEliminarAnexo(a.id, a.nombre)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '13px', flexShrink: 0, padding: 0 }} title="Eliminar">
                  <i className="ti ti-trash"></i>
                </button>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>

      {/* Subir Escaneo de Registro */}
      <div className="expediente-doc-card">
        <div className="expediente-doc-head">
          <div className="expediente-doc-head-texto">
            <div className="expediente-section-title"><i className="ti ti-scan"></i> Registro de capacitación</div>
            <p className="expediente-doc-desc">Adjunta el registro escaneado correspondiente a la capacitación de ingreso.</p>
          </div>
          <div className="expediente-doc-acciones">
            <button className="expediente-doc-btn expediente-doc-primaria" onClick={() => setModalEscaneo(true)}>
              <i className="ti ti-upload"></i>
              <span>Subir Registro Escaneado</span>
            </button>
            {detalle?.url_registro_capacitacion && detalle.url_registro_capacitacion.startsWith('https://') && (
              <a href={detalle.url_registro_capacitacion} target="_blank" rel="noreferrer" className="expediente-doc-btn expediente-doc-secundaria">
                <i className="ti ti-eye"></i>
                <span>Ver Registro</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {modalEscaneo && (
        <ModalSubirEscaneo
          empleado={empleado}
          onClose={() => setModalEscaneo(false)}
        />
      )}
    </div>
  );
}

function ExpCambios({ cambios }) {
  return (
    <div className="expediente-cambios-card" data-expediente-cambios>
      <div className="expediente-cambios-head">
        <div className="expediente-cambios-head-texto">
          <div className="expediente-section-title"><i className="ti ti-history"></i> Historial de cambios</div>
          <p className="expediente-cambios-desc">Movimientos registrados en el expediente del empleado.</p>
        </div>
        {cambios.length > 0 && (
          <span className="expediente-cambios-total">{cambios.length === 1 ? '1 cambio' : `${cambios.length} cambios`}</span>
        )}
      </div>
      {cambios.length === 0 ? (
        <div className="expediente-cambios-vacio" data-cambios-vacio>
          <i className="ti ti-history" aria-hidden="true"></i>
          <div className="expediente-cambios-vacio-titulo">Sin cambios registrados</div>
          <div className="expediente-cambios-vacio-texto">No hay movimientos registrados para este empleado.</div>
        </div>
      ) : (
        <div className="expediente-cambios-lista">
          {cambios.map(c => (
            <div className="expediente-cambio-item" key={c.id}>
              <div className="expediente-cambio-tipo">
                <span className="expediente-cambio-icono"><i className="ti ti-arrows-transfer-up"></i></span>
                <span className="expediente-cambio-titulo">{(c.tipo || '').replace(/_/g, ' ')}</span>
              </div>
              <div className="expediente-cambio-valores">
                <div className="expediente-cambio-valor expediente-cambio-anterior">
                  <div className="expediente-cambio-etiqueta">Anterior</div>
                  <div className="expediente-cambio-dato">{c.anterior || '—'}</div>
                </div>
                <i className="ti ti-arrow-right expediente-cambio-flecha" aria-hidden="true"></i>
                <div className="expediente-cambio-valor expediente-cambio-nuevo">
                  <div className="expediente-cambio-etiqueta">Nuevo</div>
                  <div className="expediente-cambio-dato">{c.nuevo || '—'}</div>
                </div>
              </div>
              <div className="expediente-cambio-estatus">
                <span className={`badge ${c.estatus === 'Pendiente_Intellisis' ? 'badge-warning' : 'badge-success'}`}>
                  {c.estatus === 'Pendiente_Intellisis' ? 'Pendiente' : 'Confirmado'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExpAcciones({ detalle, onBaja, onRevertir, onReingreso }) {
  const carpetaUrl = detalle?.carpeta_drive
    ? `https://drive.google.com/drive/folders/${detalle.carpeta_drive}`
    : null;
  const estatus = detalle?.estatus || 'Activo';
  const esActivo = estatus === 'Activo';
  const esBajaA = estatus === 'Baja_A_Operativa';
  const esBajaB = estatus === 'Baja_B_Sistema';
  const enBaja = !esActivo;

  const BAJA_LABELS = {
    Activo: null,
    Baja_A_Operativa: 'Baja A — Operativa',
    Baja_B_Sistema: 'Baja B — Sistema',
    Baja_C_IMSS: 'Baja C — IMSS',
  };

  return (
    <div>
      {/* Badge de estatus actual */}
      {enBaja && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>🚪</span>
          <div>
            <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '13px' }}>Empleado con baja aplicada</div>
            <div style={{ color: '#6b7280', fontSize: '12px' }}>Estatus actual: <strong>{BAJA_LABELS[estatus] || estatus}</strong></div>
          </div>
        </div>
      )}

      <div className="expediente-acciones-card" data-expediente-acciones>
        <div className="expediente-acciones-head">
          <div className="expediente-section-title"><i className="ti ti-adjustments"></i> Acciones del expediente</div>
          <p className="expediente-acciones-desc">Operaciones disponibles para este empleado.</p>
        </div>
        <div className="expediente-acciones-lista">
        {/* Baja A — solo para empleados activos */}
        {esActivo && (
          <ExpAccionFila tono="peligro" icono="ti-door-exit" nombre="Aplicar Baja A" tag="Operativa" descripcion="Registra la salida operativa del empleado. Pide confirmación antes de aplicarse.">
            <button type="button" className="expediente-accion-btn" aria-label="Aplicar Baja A" onClick={() => onBaja('Baja_A_Operativa')}>Aplicar</button>
          </ExpAccionFila>
        )}

        {/* Baja B — solo cuando ya está en Baja A */}
        {esBajaA && (
          <ExpAccionFila tono="peligro" icono="ti-database-off" nombre="Proceder a Baja B" tag="Sistema" descripcion="Registra la baja formal en el sistema. Pide confirmación antes de aplicarse.">
            <button type="button" className="expediente-accion-btn" aria-label="Proceder a Baja B" onClick={() => onBaja('Baja_B_Sistema')}>Proceder</button>
          </ExpAccionFila>
        )}

        {/* Baja C — solo cuando ya está en Baja B */}
        {esBajaB && (
          <ExpAccionFila tono="peligro" icono="ti-building-hospital" nombre="Proceder a Baja C" tag="IMSS" descripcion="Registra el trámite de baja ante el IMSS. Pide confirmación antes de aplicarse.">
            <button type="button" className="expediente-accion-btn" aria-label="Proceder a Baja C" onClick={() => onBaja('Baja_C_IMSS')}>Proceder</button>
          </ExpAccionFila>
        )}

        {/* Revertir Baja A → Activo */}
        {esBajaA && (
          <ExpAccionFila tono="aviso" icono="ti-arrow-back-up" nombre="Revertir a Activo" tag="Deshacer Baja A" descripcion="Regresa al empleado a Activo y elimina el registro de baja.">
            <button type="button" className="expediente-accion-btn" aria-label="Revertir a Activo" onClick={onRevertir}>Revertir</button>
          </ExpAccionFila>
        )}

        {/* Revertir Baja B → Baja A */}
        {esBajaB && (
          <ExpAccionFila tono="aviso" icono="ti-arrow-back-up" nombre="Revertir a Baja A" tag="Deshacer Baja B" descripcion="Regresa al empleado a Baja A — Operativa y limpia la fecha de Baja B.">
            <button type="button" className="expediente-accion-btn" aria-label="Revertir a Baja A" onClick={onRevertir}>Revertir</button>
          </ExpAccionFila>
        )}

        {/* Reingreso — solo para empleados con baja */}
        {enBaja && (
          <ExpAccionFila tono="positivo" icono="ti-user-check" nombre="Reingreso" tag="Mismo # empleado" descripcion="Reingresa al empleado conservando su número de empleado.">
            <button type="button" className="expediente-accion-btn" aria-label="Reingreso" onClick={onReingreso}>Reingresar</button>
          </ExpAccionFila>
        )}

        {/* Incidencias */}
        {esActivo && (
          <ExpAccionFila tono="off" icono="ti-alert-triangle" nombre="Agregar Incidencia" descripcion="Registro de incidencias del empleado.">
            <button type="button" className="expediente-accion-btn" disabled>Próximamente</button>
          </ExpAccionFila>
        )}

        {/* Drive */}
        {carpetaUrl ? (
          <ExpAccionFila tono="neutro" icono="ti-brand-google-drive" nombre="Ver carpeta en Drive" descripcion="Abre la carpeta documental del empleado en una pestaña nueva.">
            <a href={carpetaUrl} target="_blank" rel="noreferrer" className="expediente-accion-btn" aria-label="Ver carpeta en Drive">
              <span>Abrir</span><i className="ti ti-external-link"></i>
            </a>
          </ExpAccionFila>
        ) : (
          <ExpAccionFila tono="off" icono="ti-folder-off" nombre="Carpeta no disponible" descripcion="El empleado no tiene una carpeta de Drive asociada.">
            <button type="button" className="expediente-accion-btn" disabled>No disponible</button>
          </ExpAccionFila>
        )}
        </div>
      </div>
    </div>
  );
}

// Fila del centro de acciones (solo presentación): icono + nombre/descripción + control.
function ExpAccionFila({ tono, icono, nombre, tag, descripcion, children }) {
  return (
    <div className={`expediente-accion-item expediente-accion-${tono}`}>
      <span className="expediente-accion-icon"><i className={`ti ${icono}`}></i></span>
      <div className="expediente-accion-info">
        <div className="expediente-accion-titulo">
          <span className="expediente-accion-nombre">{nombre}</span>
          {tag && <span className="expediente-accion-tag">{tag}</span>}
        </div>
        <div className="expediente-accion-desc">{descripcion}</div>
      </div>
      <div className="expediente-accion-control">{children}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL REINGRESO
// ════════════════════════════════════════════════════════════════════════════
function ModalReingreso({ empleado, detalle, onClose, onDone }) {
  const d = detalle || {};
  const [tab, setTab] = useState(1);
  const [enviando, setEnviando] = useState(false);
  const [catalogoAreas, setCatalogoAreas] = useState([]);
  const [catalogoPuestos, setCatalogoPuestos] = useState([]);
  const [datos, setDatos] = useState({
    id_empleado: String(empleado.id),
    nombres: d.nombres || '',
    apellido_paterno: d.apellido_paterno || '',
    apellido_materno: d.apellido_materno || '',
    sexo: d.sexo || '',
    estado_civil: d.estado_civil || '',
    escolaridad: d.escolaridad || '',
    telefono: d.telefono || '',
    email: d.email || '',
    tiene_hijos: d.tiene_hijos || 'No',
    numero_hijos: d.numero_hijos || 0,
    fecha_nacimiento: d.fecha_nacimiento ? String(d.fecha_nacimiento).slice(0, 10) : '',
    curp: d.curp || '',
    rfc: d.rfc || '',
    nss: d.nss || '',
    sdi: d.sdi || '',
    salario_diario: d.salario_diario || '',
    sucursal_nomina: d.sucursal_nomina || '',
    area: d.area || '',
    puesto: d.puesto || '',
    tipo_empleado: d.tipo_empleado || 'Operativo',
    tipo_contrato: d.tipo_contrato || '',
    fecha_ingreso: '',
    dom_calle: d.dom_calle || '',
    dom_numero_ext: d.dom_numero_ext || '',
    dom_numero_int: d.dom_numero_int || '',
    dom_colonia: d.dom_colonia || '',
    dom_municipio: d.dom_municipio || '',
    dom_estado: d.dom_estado || '',
    dom_cp: d.dom_cp || '',
    fiscal_cp_sat: d.fiscal_cp_sat || '',
    contacto_emergencia_nombre: d.contacto_emergencia_nombre || '',
    contacto_emergencia_parentesco: d.contacto_emergencia_parentesco || '',
    contacto_emergencia_telefono: d.contacto_emergencia_telefono || '',
    beneficiario_banregio: d.beneficiario_banregio || '',
    parentesco_b: d.parentesco_b || '',
    telefono_b: d.telefono_b || '',
    lugar_nacimiento_b: d.lugar_nacimiento_b || '',
    fecha_nacimiento_b: d.fecha_nacimiento_b ? String(d.fecha_nacimiento_b).slice(0, 10) : ''
  });

  useEffect(() => {
    fetch(API_URL + '?path=api/catalogos')
      .then(r => r.json())
      .then(res => { if (res.success) { setCatalogoAreas(res.areas || []); setCatalogoPuestos(res.puestos || []); } })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDatos(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'sucursal_nomina') next.dom_estado = SUCURSAL_ESTADO[value] || prev.dom_estado;
      if (name === 'dom_estado') next.dom_municipio = '';
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!datos.fecha_ingreso) { alert('⚠️ La nueva fecha de ingreso es requerida'); return; }
    setEnviando(true);
    try {
      const payload = encodeURIComponent(JSON.stringify(payloadConBeneficiario(datos, d)));
      const res = await fetch(API_URL + `?path=api/empleado/reingreso&payload=${payload}`);
      const result = await res.json();
      if (result.success) {
        alert('✅ Reingreso aplicado correctamente');
        await onDone();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setEnviando(false);
    }
  };

  const municipios = MUNICIPIOS_POR_ESTADO[datos.dom_estado] || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '640px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔄 Reingreso — #{empleado.id} {empleado.nombre}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '10px 16px', margin: '0 24px 16px' }}>
          <p style={{ fontSize: '13px', color: '#065f46', margin: 0 }}>
            El # de empleado <strong>#{empleado.id}</strong> se conserva. Confirma o modifica los datos — todos los campos son editables, incluyendo salario, SDI y fecha de ingreso.
          </p>
        </div>

        <div className="modal-tabs">
          {[{ num: 1, label: 'Personal' }, { num: 2, label: 'Laboral' }, { num: 3, label: 'Domicilio' }, { num: 4, label: 'Beneficiario' }].map(t => (
            <button key={t.num} className={`modal-tab ${tab === t.num ? 'active' : ''}`} onClick={() => setTab(t.num)}>{t.label}</button>
          ))}
        </div>

        <div className="modal-body">
          {tab === 1 && (
            <div className="form-grid">
              <Input label="Nombres *" name="nombres" value={datos.nombres} onChange={handleChange} />
              <Input label="Apellido Paterno *" name="apellido_paterno" value={datos.apellido_paterno} onChange={handleChange} />
              <Input label="Apellido Materno" name="apellido_materno" value={datos.apellido_materno} onChange={handleChange} />
              <Select label="Género" name="sexo" value={datos.sexo} onChange={handleChange} opciones={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Femenino' }]} />
              <Select label="Estado Civil" name="estado_civil" value={datos.estado_civil} onChange={handleChange} opciones={['Soltero', 'Casado', 'Unión libre', 'Viudo', 'Divorciado']} />
              <Select label="Escolaridad" name="escolaridad" value={datos.escolaridad} onChange={handleChange} opciones={['Primaria', 'Secundaria', 'Preparatoria', 'Técnico', 'Licenciatura']} />
              <Input label="Teléfono" name="telefono" value={datos.telefono} onChange={handleChange} />
              <Input label="Email" name="email" value={datos.email} onChange={handleChange} type="email" />
              <Select label="¿Tiene Hijos?" name="tiene_hijos" value={datos.tiene_hijos} onChange={handleChange} opciones={['Sí', 'No']} />
              {datos.tiene_hijos === 'Sí' && <Input label="Número de Hijos" name="numero_hijos" value={datos.numero_hijos} onChange={handleChange} type="number" />}
              <Input label="Fecha de Nacimiento" name="fecha_nacimiento" value={datos.fecha_nacimiento} onChange={handleChange} type="date" />
            </div>
          )}
          {tab === 2 && (
            <div className="form-grid">
              <Input label="CURP" name="curp" value={datos.curp} onChange={handleChange} maxLength="18" />
              <Input label="RFC" name="rfc" value={datos.rfc} onChange={handleChange} maxLength="13" />
              <Input label="NSS" name="nss" value={datos.nss} onChange={handleChange} />
              <Input label="SDI *" name="sdi" value={datos.sdi} onChange={handleChange} />
              <Input label="Salario Diario *" name="salario_diario" value={datos.salario_diario} onChange={handleChange} type="number" step="0.01" />
              <Input label="Nueva Fecha de Ingreso *" name="fecha_ingreso" value={datos.fecha_ingreso} onChange={handleChange} type="date" />
              <Select label="Sucursal" name="sucursal_nomina" value={datos.sucursal_nomina} onChange={handleChange} opciones={OPCIONES_SUCURSALES} />
              <Select label="Área" name="area" value={datos.area} onChange={handleChange} opciones={catalogoAreas} />
              <Select label="Puesto" name="puesto" value={datos.puesto} onChange={handleChange} opciones={catalogoPuestos} />
              <Select label="Tipo Empleado" name="tipo_empleado" value={datos.tipo_empleado} onChange={handleChange} opciones={['Operativo', 'Administrativo']} />
              <Select label="Tipo Contrato" name="tipo_contrato" value={datos.tipo_contrato} onChange={handleChange} opciones={['1er 3 meses', '2do 3 meses', 'Indefinido']} />
            </div>
          )}
          {tab === 3 && (
            <div className="form-grid">
              <Input label="Calle" name="dom_calle" value={datos.dom_calle} onChange={handleChange} />
              <Input label="Número Exterior" name="dom_numero_ext" value={datos.dom_numero_ext} onChange={handleChange} />
              <Input label="Número Interior" name="dom_numero_int" value={datos.dom_numero_int} onChange={handleChange} />
              <Input label="Colonia" name="dom_colonia" value={datos.dom_colonia} onChange={handleChange} />
              <Select label="Estado" name="dom_estado" value={datos.dom_estado} onChange={handleChange} opciones={Object.keys(MUNICIPIOS_POR_ESTADO)} />
              <Select label="Municipio" name="dom_municipio" value={datos.dom_municipio} onChange={handleChange} opciones={municipios} disabled={!datos.dom_estado} />
              <Input label="CP" name="dom_cp" value={datos.dom_cp} onChange={handleChange} />
              <Input label="CP SAT" name="fiscal_cp_sat" value={datos.fiscal_cp_sat} onChange={handleChange} />
              <h3 style={{ gridColumn: '1 / -1', marginTop: '8px' }}>Contacto de Emergencia</h3>
              <Input label="Nombre" name="contacto_emergencia_nombre" value={datos.contacto_emergencia_nombre} onChange={handleChange} />
              <Input label="Parentesco" name="contacto_emergencia_parentesco" value={datos.contacto_emergencia_parentesco} onChange={handleChange} />
              <Input label="Teléfono" name="contacto_emergencia_telefono" value={datos.contacto_emergencia_telefono} onChange={handleChange} />
            </div>
          )}
          {tab === 4 && (
            <div className="form-grid">
              <Input label="Nombre Beneficiario" name="beneficiario_banregio" value={datos.beneficiario_banregio} onChange={handleChange} />
              <Input label="Parentesco" name="parentesco_b" value={datos.parentesco_b} onChange={handleChange} />
              <Input label="Teléfono" name="telefono_b" value={datos.telefono_b} onChange={handleChange} />
              <Input label="Lugar de Nacimiento" name="lugar_nacimiento_b" value={datos.lugar_nacimiento_b} onChange={handleChange} placeholder="Ciudad, Estado" />
              <Input label="Fecha de Nacimiento" name="fecha_nacimiento_b" value={datos.fecha_nacimiento_b} onChange={handleChange} type="date" />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={enviando}>
            {enviando ? 'Procesando...' : '🔄 Confirmar Reingreso'}
          </button>
        </div>
      </div>
    </div>
  );
}
